import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { DialogService } from 'src/app/dialog/dialog.service';
import { DirectionService } from 'src/app/direction.service';
import { ClustersService } from 'src/app/main-section/choose-branch/clusters.service';
import { UsersService } from 'src/app/super-management/user/users.service';
import {
  ExhibitionComment,
  ExhibitionFeedItem,
  ExhibitionOwnerProfile,
  WallApiService,
  isWallPlaceholderThumb,
  bindWallScrollCollapse,
  isWallDesktopViewport,
  isWallCommentAuthorNameTruncated,
  wallCommentAuthorDisplayName,
  isWallImagePanoramic,
  wallDisplayedSize,
  wallFocusMediaEl,
  wallFocusPinPanelToBox,
  wallFocusNaturalPanelHeight,
  wallFocusNatFromPanelImg,
  wallFocusPlayCloseToSlot,
  wallFocusPlayOpenFromSlot,
  wallFocusClearMediaFlipStyles,
  kickWallLazyImagesNearView,
  hydrateWallItemImageSizes,
  wallDisplayedAspectChanged,
  nextWallProfileImage,
  wallProfileImageCandidates,
  wallCommentTimeLabel,
  scrollWallMediaToVisibleTop,
  scrollWallFocusElIntoView,
  toPublicWallThumb,
  resolveWallItemThumb,
  wallOverlayNameClipMax,
  clipWallOverlayName,
} from '../wall-api.service';
import {
  GalleryLayoutRow,
  GalleryRowsLayoutService,
  GALLERY_ROWS_TUNABLES,
  WallSkeletonWork,
  isWallSkeletonWork,
  makeWallSkeletonWorks,
  wallSkeletonCount,
} from '../gallery-rows-layout.service';
import {
  WALL_VISIBLE_COMMENTS_LIMIT,
  VisibleCommentThread,
  allCommentThreads,
  sliceVisibleCommentThreads,
  wallCommentsLoadReservePx,
} from '../wall-comments-visible';

/** Survives component recreate when navigating exhibition → /exhibition/:userId. */
let pendingOwnerPreview: ExhibitionOwnerProfile | null = null;

export interface ExhibitionCard extends ExhibitionFeedItem {
  commentsOpen: boolean;
  commentsMounted: boolean;
  commentsLoading: boolean;
  comments: ExhibitionComment[];
  draftComment: string;
  liking: boolean;
  commenting: boolean;
  likers?: string[];
  likersExpanded?: boolean;
  workTitleExpanded?: boolean;
  workDescExpanded?: boolean;
  workTitleOverflow?: boolean;
  workDescOverflow?: boolean;
  statusTip?: 'looking' | 'helping' | null;
  imgLoaded?: boolean;
  /** Natural pixel size — used to size media box after CSS rotation */
  natW?: number;
  natH?: number;
}

@Component({
  selector: 'app-eazix-exhibition',
  templateUrl: './eazix-exhibition.component.html',
  styleUrls: ['./eazix-exhibition.component.css'],
  host: {
    class: 'fill-screen eazix-exhibition-host',
    '[class.eazix-exhibition-host-dark]': 'isDarkMode',
    '[class.eazix-exhibition-host-focus]': '!!commentsTargetId',
  },
})
export class EazixExhibitionComponent implements OnInit, AfterViewInit, OnDestroy {
  isRTL = true;
  isDarkMode = false;
  isAuth = false;
  haveWall = false;
  isScrolledDown = false;

  private scrollListener: (() => void) | null = null;

  cards: ExhibitionCard[] = [];
  skeletonSlots: WallSkeletonWork[] = [];
  layoutRows: GalleryLayoutRow<ExhibitionCard | WallSkeletonWork>[] = [];
  layoutTotalHeight = 0;
  layoutGap: number = GALLERY_ROWS_TUNABLES.gapDesktop;
  isLoading = true;
  isLoadingMore = false;
  hasMore = true;
  loadError = false;

  commentsTargetId: string | null = null;
  filterUserId: string | null = null;
  filterProfile: ExhibitionOwnerProfile | null = null;

  /** Main exhibition feed filters (hidden on a single-user wall). */
  filterPanelOpen = false;
  filterAcademy = '';
  filterLooking = false;
  filterHelping = false;
  academyOptions: { serialName: string; labelKey: string }[] = [];
  private academyOptionsLoaded = false;
  private clustersSub: Subscription;

  @ViewChildren('sentinel') sentinels?: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('collage') collageRef?: ElementRef<HTMLElement>;

  private directionSub: Subscription;
  private darkSub: Subscription;
  private authSub: Subscription;
  private routeSub: Subscription;
  private feedSub: Subscription;
  private sentinelChangesSub: Subscription;
  private likeSubs: { [id: string]: Subscription } = {};
  private commentLikeSubs: { [id: string]: Subscription } = {};
  private commentSubs: { [id: string]: Subscription } = {};
  private replySubs: { [id: string]: Subscription } = {};
  private deleteCommentSubs: { [id: string]: Subscription } = {};
  private editCommentSubs: { [id: string]: Subscription } = {};
  private loadCommentsSubs: { [id: string]: Subscription } = {};
  private loadLikersSubs: { [id: string]: Subscription } = {};
  private loadCommentLikersSubs: { [id: string]: Subscription } = {};
  private prefetchCommenterSubs: { [id: string]: Subscription } = {};
  private ownerImageSub: Subscription;
  private observer: IntersectionObserver | null = null;
  private layoutObserver: ResizeObserver | null = null;
  private feedCatchUpRaf: number | null = null;
  private balanceRelayoutTimer: ReturnType<typeof setTimeout> | null = null;
  private resizeLayoutTimer: ReturnType<typeof setTimeout> | null = null;
  private lastLayoutWidth = 0;
  private seenIds = new Set<string>();
  private readonly pageSize = 24;
  private commentsCloseTimers: { [id: string]: ReturnType<typeof setTimeout> } = {};
  private statusTipTimers: { [id: string]: ReturnType<typeof setTimeout> } = {};
  private readonly commentsAnimMs = 480;
  private bodyOverflowPrev: string | null = null;
  private focusFlipUntil = 0;
  private focusAdaptQueued = false;
  private focusCappedIds = new Set<string>();
  private focusAdaptRaf: number | null = null;
  private focusOpenRaf: number | null = null;
  private focusSettleTimer: ReturnType<typeof setTimeout> | null = null;
  private feedHydrateGen = 0;
  private focusFlipOrigin: {
    left: number;
    top: number;
    width: number;
    height: number;
  } | null = null;
  private readonly onFocusOutsidePointerDown = (event: PointerEvent): void => {
    this.handleFocusOutsidePointerDown(event);
  };
  private readonly onFilterOutsidePointerDown = (event: PointerEvent): void => {
    this.handleFilterOutsidePointerDown(event);
  };
  private readonly onFocusViewportChange = (): void => {
    this.scheduleFocusAdapt();
    this.scrollAnyOpenReplySendIntoView();
  };

  constructor(
    private host: ElementRef<HTMLElement>,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private directionService: DirectionService,
    private wallApi: WallApiService,
    private usersService: UsersService,
    private clustersService: ClustersService,
    private rowsLayout: GalleryRowsLayoutService,
    private authService: AuthService,
    private dialogService: DialogService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.lockStableScrollbarGutter();
    this.setFeedSkeletons(true);
    this.directionSub = this.directionService.direction$.subscribe((direction) => {
      this.isRTL = direction === 'rtl';
    });
    this.darkSub = this.directionService.isDarkMode$.subscribe((isDarkMode) => {
      this.isDarkMode = isDarkMode;
    });
    this.isAuth = this.authService.getIsAuth();
    this.haveWall = localStorage.getItem('haveWall') === 'true';
    this.authSub = this.authService
      .getAuthStatusListener()
      .subscribe((isAuth) => {
        this.isAuth = isAuth;
        this.haveWall = isAuth && localStorage.getItem('haveWall') === 'true';
      });
    this.routeSub = this.route.paramMap
      .pipe(
        map((params) => String(params.get('userId') || '').trim()),
        distinctUntilChanged()
      )
      .subscribe((userId) => {
        this.onRouteUserId(userId);
      });
    if (!String(this.route.snapshot.paramMap.get('userId') || '').trim()) {
      this.loadAcademyOptions();
    }
  }

  ngAfterViewInit(): void {
    this.sentinelChangesSub = this.sentinels?.changes.subscribe(() => {
      this.setupObserver();
    });
    this.setupObserver();
    this.bindScrollListener();
    this.bindLayoutObserver();
    this.bindFocusViewport();
    document.addEventListener('pointerdown', this.onFocusOutsidePointerDown, true);
    document.addEventListener('pointerdown', this.onFilterOutsidePointerDown, true);
  }

  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  onWindowResize(): void {
    this.scheduleRowsRelayoutFromResize();
    this.queueFeedCatchUp();
    this.scheduleFocusAdapt();
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseComments(): void {
    if (this.filterPanelOpen) {
      this.closeFilterPanel();
      return;
    }
    if (!this.commentsTargetId) {
      return;
    }
    const card = this.cards.find((c) => c._id === this.commentsTargetId);
    if (card && (card.commentsOpen || card.commentsMounted)) {
      this.closeCommentsPanel(card);
    }
  }

  @HostListener('document:wall-feed-catch-up')
  onWallFeedCatchUp(): void {
    this.queueFeedCatchUp();
  }

  private bindScrollListener(): void {
    this.unbindScrollListener();
    this.scrollListener = bindWallScrollCollapse(
      this.host.nativeElement,
      (scrolled) => {
        this.ngZone.run(() => {
          this.isScrolledDown = scrolled;
        });
      },
    );
  }

  private unbindScrollListener(): void {
    this.scrollListener?.();
    this.scrollListener = null;
  }

  private bindLayoutObserver(): void {
    this.unbindLayoutObserver();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.layoutObserver = new ResizeObserver(() => {
      this.scheduleRowsRelayoutFromResize();
      this.queueFeedCatchUp();
    });
    const target = this.collageRef?.nativeElement || this.host.nativeElement;
    this.layoutObserver.observe(target);
  }

  private unbindLayoutObserver(): void {
    this.layoutObserver?.disconnect();
    this.layoutObserver = null;
  }

  private bindFocusViewport(): void {
    this.unbindFocusViewport();
    if (typeof window === 'undefined') {
      return;
    }
    window.visualViewport?.addEventListener('resize', this.onFocusViewportChange);
    window.visualViewport?.addEventListener('scroll', this.onFocusViewportChange);
  }

  private unbindFocusViewport(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.visualViewport?.removeEventListener('resize', this.onFocusViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.onFocusViewportChange);
  }

  shareNavTooltipEnabled(): boolean {
    return this.isScrolledDown && isWallDesktopViewport();
  }

  ngOnDestroy(): void {
    this.unlockStableScrollbarGutter();
    this.directionSub?.unsubscribe();
    this.darkSub?.unsubscribe();
    this.authSub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.feedSub?.unsubscribe();
    this.ownerImageSub?.unsubscribe();
    this.clustersSub?.unsubscribe();
    this.sentinelChangesSub?.unsubscribe();
    this.unbindScrollListener();
    this.unbindLayoutObserver();
    this.unbindFocusViewport();
    document.removeEventListener('pointerdown', this.onFocusOutsidePointerDown, true);
    document.removeEventListener('pointerdown', this.onFilterOutsidePointerDown, true);
    this.unlockBodyScroll();
    this.observer?.disconnect();
    if (this.feedCatchUpRaf != null) {
      cancelAnimationFrame(this.feedCatchUpRaf);
      this.feedCatchUpRaf = null;
    }
    if (this.focusAdaptRaf != null) {
      cancelAnimationFrame(this.focusAdaptRaf);
      this.focusAdaptRaf = null;
    }
    this.cancelPendingFocusOpen();
    if (this.balanceRelayoutTimer) {
      clearTimeout(this.balanceRelayoutTimer);
      this.balanceRelayoutTimer = null;
    }
    if (this.resizeLayoutTimer) {
      clearTimeout(this.resizeLayoutTimer);
      this.resizeLayoutTimer = null;
    }
    Object.values(this.likeSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.commentLikeSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.commentSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.replySubs).forEach((s) => s?.unsubscribe());
    Object.values(this.deleteCommentSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.editCommentSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.loadCommentsSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.loadLikersSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.loadCommentLikersSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.prefetchCommenterSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.commentsCloseTimers).forEach((t) => clearTimeout(t));
    Object.values(this.statusTipTimers).forEach((t) => clearTimeout(t));
    this.observer?.disconnect();
  }

  trackById(_index: number, card: ExhibitionCard): string {
    return card._id;
  }

  trackRow(
    index: number,
    row: GalleryLayoutRow<ExhibitionCard | WallSkeletonWork>
  ): number {
    return row?.zIndex ?? index;
  }

  trackLayoutItem(
    _index: number,
    item: { work: ExhibitionCard | WallSkeletonWork }
  ): string {
    return item.work._id;
  }

  isSkeletonWork(work: unknown): work is WallSkeletonWork {
    return isWallSkeletonWork(work);
  }

  trackCommentById(_index: number, c: ExhibitionComment): string {
    return c._id;
  }

  trackCommentThread(_index: number, thread: VisibleCommentThread): string {
    return thread?.comment?._id || String(_index);
  }

  visibleComments(card: ExhibitionCard): VisibleCommentThread[] {
    if (card?.commentsMounted) {
      return allCommentThreads(card.comments);
    }
    return sliceVisibleCommentThreads(card?.comments, WALL_VISIBLE_COMMENTS_LIMIT);
  }

  commentInitial(comment: ExhibitionComment): string {
    const name = String(comment?.authorName || '').trim();
    return (name.charAt(0) || '?').toUpperCase();
  }

  shortAuthorName(name?: string): string {
    const n = String(name || '').trim();
    if (n.length <= 15) {
      return n;
    }
    return `${n.slice(0, 15)}...`;
  }

  /** Author on card overlay; shorter on extreme aspect ratios. */
  cardOverlayAuthorName(card: ExhibitionCard): string {
    const name = String(card?.authorName || '').trim();
    const max = wallOverlayNameClipMax(card?.natW, card?.natH, card?.rotation);
    if (max != null) {
      return clipWallOverlayName(name, max);
    }
    return this.shortAuthorName(name);
  }

  /** Academy on card overlay; shorter on extreme aspect ratios. */
  cardOverlayAcademyName(card: ExhibitionCard): string {
    const label = this.fullAcademyLabel(card?.academySerialName);
    const max = wallOverlayNameClipMax(card?.natW, card?.natH, card?.rotation);
    if (max != null) {
      return clipWallOverlayName(label, max);
    }
    return label;
  }

  /** Likers list: truncate above 8 chars. */
  likerDisplayName(name?: string): string {
    const n = String(name || '').trim();
    if (n.length <= 8) {
      return n;
    }
    return `${n.slice(0, 8)}...`;
  }

  commentAuthorDisplayName(name?: string): string {
    return wallCommentAuthorDisplayName(name);
  }

  isCommentAuthorNameTruncated(name?: string): boolean {
    return isWallCommentAuthorNameTruncated(name);
  }

  showCommentTextMore(comment: ExhibitionComment): boolean {
    return (
      !!comment?._id &&
      !this.isCommentTextExpanded(comment) &&
      this.overflowCommentTextIds.has(comment._id)
    );
  }

  showCommentTextLess(comment: ExhibitionComment): boolean {
    return (
      !!comment?._id &&
      this.isCommentTextExpanded(comment) &&
      this.overflowCommentTextIds.has(comment._id)
    );
  }

  onCommentTextOverflow(commentId: string, overflowing: boolean): void {
    if (!commentId) {
      return;
    }
    if (overflowing) {
      if (!this.overflowCommentTextIds.has(commentId)) {
        this.overflowCommentTextIds.add(commentId);
        this.scheduleFocusAdapt();
      }
      return;
    }
    if (!this.expandedCommentTextIds.has(commentId)) {
      this.overflowCommentTextIds.delete(commentId);
    }
    this.scheduleFocusAdapt();
    this.cdr.markForCheck();
  }

  expandCommentText(comment: ExhibitionComment, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (comment?._id) {
      this.expandedCommentTextIds.add(comment._id);
    }
  }

  collapseCommentText(comment: ExhibitionComment, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (comment?._id) {
      this.expandedCommentTextIds.delete(comment._id);
    }
  }

  isCommentTextExpanded(comment: ExhibitionComment): boolean {
    return !!comment?._id && this.expandedCommentTextIds.has(comment._id);
  }

  private forgetCommentTextLayout(commentId?: string): void {
    if (!commentId) {
      return;
    }
    this.expandedCommentTextIds.delete(commentId);
    this.overflowCommentTextIds.delete(commentId);
  }

  private afterCommentsLayoutChange(): void {
    this.cdr.detectChanges();
    this.scheduleFocusAdapt();
  }

  private readonly expandedCommentTextIds = new Set<string>();
  private readonly overflowCommentTextIds = new Set<string>();
  private readonly composeHadTextIds = new Set<string>();

  isLikerNameTruncated(name?: string): boolean {
    return String(name || '').trim().length > 8;
  }

  /** Click shows full truncated name (desktop + mobile). Leave hover to close. */
  showTruncatedNameTip(event: Event, tip: MatTooltip, name?: string): void {
    if (!String(name || '').trim()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    tip.show(0);
  }

  hideTruncatedNameTip(tip: MatTooltip): void {
    tip?.hide(0);
  }

  clipText(value?: string, max = 20): string {
    const t = String(value || '').trim();
    if (t.length <= max) {
      return t;
    }
    return `${t.slice(0, max)}...`;
  }

  ownerTitleClipMax(): number {
    return typeof window !== 'undefined' && window.innerWidth <= 640 ? 10 : 30;
  }

  filterTitleName(): string {
    const raw =
      String(this.filterProfile?.authorName || '').trim() ||
      this.translate.instant('wall.personal.anonymous');
    return this.clipText(raw, this.ownerTitleClipMax());
  }

  filterOwnerInitial(): string {
    const name =
      String(this.filterProfile?.authorName || '').trim() ||
      this.translate.instant('wall.personal.anonymous');
    return name.charAt(0) || '?';
  }

  onFilterOwnerAvatarError(): void {
    if (!this.filterProfile) {
      return;
    }
    const next = nextWallProfileImage(
      this.filterProfile.authorImageURL,
      this.filterProfile.authorImageCandidates
    );
    this.filterProfile = {
      ...this.filterProfile,
      authorImageURL: next,
    };
    this.cdr.detectChanges();
  }

  /** Merge profile-image candidate lists without dropping already-known URLs. */
  private mergeImageCandidates(
    ...lists: Array<string[] | string | null | undefined>
  ): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const list of lists) {
      const values = Array.isArray(list)
        ? list
        : list
          ? [list]
          : [];
      for (const raw of values) {
        const u = String(raw || '').trim();
        if (!u || seen.has(u)) {
          continue;
        }
        seen.add(u);
        out.push(u);
      }
    }
    return out;
  }

  /** Apply / enrich the user-wall title avatar (keeps any better URL already shown). */
  private applyFilterOwnerImage(
    url?: string | null,
    candidates?: string[] | null
  ): boolean {
    if (!this.filterProfile) {
      return false;
    }
    const merged = this.mergeImageCandidates(
      candidates,
      this.filterProfile.authorImageCandidates,
      url
    );
    const nextUrl =
      String(url || '').trim() ||
      String(this.filterProfile.authorImageURL || '').trim() ||
      merged[0] ||
      '';
    if (!nextUrl) {
      return false;
    }
    const prevUrl = String(this.filterProfile.authorImageURL || '').trim();
    const prevCandidates = this.filterProfile.authorImageCandidates || [];
    const candidatesChanged =
      merged.length !== prevCandidates.length ||
      merged.some((u, i) => u !== prevCandidates[i]);
    if (nextUrl === prevUrl && !candidatesChanged) {
      return true;
    }
    this.filterProfile = {
      ...this.filterProfile,
      authorImageURL: nextUrl,
      authorImageCandidates: merged.length ? merged : [nextUrl],
    };
    this.cdr.detectChanges();
    return true;
  }

  /** Same image source comments already return for this wall owner. */
  private syncOwnerAvatarFromComments(
    comments: ExhibitionComment[] | null | undefined
  ): void {
    const ownerId = String(this.filterUserId || '').trim();
    if (!ownerId || !this.filterProfile) {
      return;
    }
    if (String(this.filterProfile.authorImageURL || '').trim()) {
      return;
    }
    const flat: ExhibitionComment[] = [];
    const walk = (list: ExhibitionComment[]) => {
      for (const c of list || []) {
        flat.push(c);
        if (c.replies?.length) {
          walk(c.replies);
        }
      }
    };
    walk(comments || []);
    const hit = flat.find(
      (c) =>
        String(c.userID || '').trim() === ownerId &&
        String(c.authorImageURL || '').trim()
    );
    if (!hit) {
      return;
    }
    this.applyFilterOwnerImage(hit.authorImageURL, hit.authorImageCandidates);
  }

  /**
   * api-dev exhibition profile has no authorImageURL yet; comments API does.
   * Quietly reuse that when the wall owner has commented on their own work.
   */
  private prefetchOwnerAvatarFromComments(): void {
    const ownerId = String(this.filterUserId || '').trim();
    if (!ownerId || !this.filterProfile) {
      return;
    }
    if (String(this.filterProfile.authorImageURL || '').trim()) {
      return;
    }
    const card = this.cards.find(
      (c) => String(c.userID || '').trim() === ownerId
    );
    if (!card?._id) {
      return;
    }
    if (card.comments?.length) {
      this.syncOwnerAvatarFromComments(card.comments);
      return;
    }
    this.prefetchCommenterSubs['owner-avatar']?.unsubscribe();
    this.prefetchCommenterSubs['owner-avatar'] = this.wallApi
      .getItemComments(card._id)
      .subscribe({
        next: (res) => {
          if (
            String(this.filterUserId || '').trim() !== ownerId ||
            !this.filterProfile
          ) {
            return;
          }
          const incoming = Array.isArray(res?.comments) ? res.comments : [];
          if (!card.comments.length) {
            card.comments = this.nestComments(incoming);
          }
          this.syncOwnerAvatarFromComments(
            card.comments.length ? card.comments : this.nestComments(incoming)
          );
        },
        error: () => {},
      });
  }

  isContactAllowed(flag?: boolean): boolean {
    return flag !== false;
  }

  isOwnUser(userID?: string): boolean {
    const id = String(userID || '').trim();
    const me = String(this.authService.getUserId() || '').trim();
    return !!id && !!me && id === me;
  }

  displayWorkText(value: string | undefined, _card?: ExhibitionCard): string {
    return String(value || '').trim();
  }

  isWorkTitleOverflow(card: ExhibitionCard): boolean {
    return !!card.workTitleOverflow;
  }

  isWorkDescOverflow(card: ExhibitionCard): boolean {
    return !!card.workDescOverflow;
  }

  onWorkTitleOverflow(card: ExhibitionCard, overflowing: boolean): void {
    card.workTitleOverflow = overflowing;
  }

  onWorkDescOverflow(card: ExhibitionCard, overflowing: boolean): void {
    card.workDescOverflow = overflowing;
  }

  toggleWorkText(card: ExhibitionCard, field: 'title' | 'description', event?: Event): void {
    event?.stopPropagation();
    if (field === 'title') {
      card.workTitleExpanded = !card.workTitleExpanded;
    } else {
      card.workDescExpanded = !card.workDescExpanded;
    }
    if (this.commentsTargetId === card._id && card.commentsOpen) {
      this.cdr.detectChanges();
      this.afterFocusContentLayout(card._id);
    }
  }

  onStatusBadgeClick(
    card: ExhibitionCard,
    which: 'looking' | 'helping',
    event: Event
  ): void {
    event.stopPropagation();
    if (!this.isTouchUi()) {
      return;
    }
    const next = card.statusTip === which ? null : which;
    this.cards.forEach((c) => {
      if (c._id !== card._id) {
        this.clearStatusTip(c);
      }
    });
    this.clearStatusTipTimer(card._id);
    card.statusTip = next;
    if (next) {
      this.statusTipTimers[card._id] = setTimeout(() => {
        if (card.statusTip === next) {
          card.statusTip = null;
        }
        delete this.statusTipTimers[card._id];
      }, 2800);
    }
  }

  private isTouchUi(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    if (window.innerWidth <= 640) {
      return true;
    }
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: none)').matches
    );
  }

  private clearStatusTip(card: ExhibitionCard): void {
    card.statusTip = null;
    this.clearStatusTipTimer(card._id);
  }

  private clearStatusTipTimer(id: string): void {
    const t = this.statusTipTimers[id];
    if (t) {
      clearTimeout(t);
      delete this.statusTipTimers[id];
    }
  }

  commentTimeLabel(comment: ExhibitionComment): string {
    return wallCommentTimeLabel(comment?.created);
  }

  onCommentAvatarError(comment: ExhibitionComment): void {
    comment.authorImageURL = nextWallProfileImage(
      comment.authorImageURL,
      comment.authorImageCandidates
    );
  }

  academyLabelKey(serialName: string): string {
    const sn = String(serialName || '').trim();
    if (!sn || sn === 'express' || sn === 'plotter') {
      return '';
    }
    return `choose-branch.naming.${sn}`;
  }

  fullAcademyLabel(serialName?: string): string {
    const sn = String(serialName || '').trim();
    const key = this.academyLabelKey(sn);
    if (!key) {
      return '';
    }
    const translated = String(this.translate.instant(key) || '').trim();
    if (translated && translated !== key) {
      return translated;
    }
    return sn;
  }

  academyLogoUrl(serialName: string): string {
    const sn = String(serialName || '').trim();
    return sn ? `assets/images/branches/${sn}.png` : '';
  }

  onAcademyLogoError(event: Event): void {
    const img = event?.target as HTMLImageElement | null;
    if (img) {
      img.style.display = 'none';
    }
  }

  activeFeedFilterCount(): number {
    let n = 0;
    if (String(this.filterAcademy || '').trim()) {
      n += 1;
    }
    if (this.filterLooking) {
      n += 1;
    }
    if (this.filterHelping) {
      n += 1;
    }
    return n;
  }

  hasActiveFeedFilters(): boolean {
    return this.activeFeedFilterCount() > 0;
  }

  toggleFilterPanel(): void {
    if (this.filterUserId) {
      return;
    }
    this.filterPanelOpen = !this.filterPanelOpen;
    if (this.filterPanelOpen) {
      this.loadAcademyOptions();
    }
  }

  closeFilterPanel(): void {
    this.filterPanelOpen = false;
  }

  private handleFilterOutsidePointerDown(event: PointerEvent): void {
    if (!this.filterPanelOpen) {
      return;
    }
    const target = event.target as Element | null;
    if (!target) {
      return;
    }
    const slot = this.host.nativeElement.querySelector(
      '.eazix-exhibition-filter-slot'
    );
    if (slot?.contains(target)) {
      return;
    }
    // Keep open while interacting with mat-select overlay options.
    if (typeof target.closest === 'function' && target.closest('.cdk-overlay-container')) {
      return;
    }
    this.ngZone.run(() => {
      this.closeFilterPanel();
      this.cdr.detectChanges();
    });
  }

  onFilterAcademyChange(serialName: string): void {
    this.filterAcademy = String(serialName || '').trim();
    this.reloadWithFeedFilters();
  }

  toggleFilterLooking(): void {
    this.filterLooking = !this.filterLooking;
    this.reloadWithFeedFilters();
  }

  toggleFilterHelping(): void {
    this.filterHelping = !this.filterHelping;
    this.reloadWithFeedFilters();
  }

  clearFeedFilterAcademy(): void {
    if (!this.filterAcademy) {
      return;
    }
    this.filterAcademy = '';
    this.reloadWithFeedFilters();
  }

  clearFeedFilterLooking(): void {
    if (!this.filterLooking) {
      return;
    }
    this.filterLooking = false;
    this.reloadWithFeedFilters();
  }

  clearFeedFilterHelping(): void {
    if (!this.filterHelping) {
      return;
    }
    this.filterHelping = false;
    this.reloadWithFeedFilters();
  }

  clearAllFeedFilters(): void {
    if (!this.hasActiveFeedFilters()) {
      return;
    }
    this.filterAcademy = '';
    this.filterLooking = false;
    this.filterHelping = false;
    this.reloadWithFeedFilters();
  }

  private reloadWithFeedFilters(): void {
    if (this.filterUserId) {
      return;
    }
    this.scrollExhibitionTop();
    this.loadInitial();
  }

  private currentFeedFilters(): {
    academySerialName?: string;
    lookingForWork?: boolean;
    offeringHelp?: boolean;
  } | null {
    if (this.filterUserId) {
      return null;
    }
    const academy = String(this.filterAcademy || '').trim();
    const looking = this.filterLooking === true;
    const helping = this.filterHelping === true;
    if (!academy && !looking && !helping) {
      return null;
    }
    return {
      ...(academy ? { academySerialName: academy } : {}),
      ...(looking ? { lookingForWork: true } : {}),
      ...(helping ? { offeringHelp: true } : {}),
    };
  }

  /** Client-side safety net while api-dev may not yet honor filter body fields. */
  private itemMatchesFeedFilters(item: ExhibitionFeedItem | ExhibitionCard): boolean {
    const filters = this.currentFeedFilters();
    if (!filters) {
      return true;
    }
    const academy = String(filters.academySerialName || '').trim();
    if (
      academy &&
      String(item?.academySerialName || '').trim() !== academy
    ) {
      return false;
    }
    const wantLooking = filters.lookingForWork === true;
    const wantHelping = filters.offeringHelp === true;
    if (wantLooking || wantHelping) {
      const ok =
        (wantLooking && item?.lookingForWork === true) ||
        (wantHelping && item?.offeringHelp === true);
      if (!ok) {
        return false;
      }
    }
    return true;
  }

  private loadAcademyOptions(): void {
    if (this.academyOptionsLoaded) {
      return;
    }
    this.academyOptionsLoaded = true;
    this.clustersSub?.unsubscribe();
    this.clustersSub = this.clustersService.getClusters().subscribe({
      next: (clusters) => {
        this.academyOptions = (clusters || [])
          .filter((cluster: any) => {
            const plotters = Array.isArray(cluster?.plotter)
              ? cluster.plotter
              : cluster?.plotter
                ? [cluster.plotter]
                : [];
            return plotters.some(
              (branch: any) => branch && branch.isNotAcademy !== true
            );
          })
          .map((cluster: any) => {
            const serialName = String(
              cluster?.serial_name || cluster?.serialName || ''
            ).trim();
            return {
              serialName,
              labelKey: `choose-branch.naming.${serialName}`,
            };
          })
          .filter((opt) => !!opt.serialName)
          .sort((a, b) =>
            this.fullAcademyLabel(a.serialName).localeCompare(
              this.fullAcademyLabel(b.serialName),
              'he'
            )
          );
        this.cdr.detectChanges();
      },
      error: () => {
        this.academyOptionsLoaded = false;
      },
    });
  }

  imageTransform(rotation?: number): string {
    const r = this.normalizeRotation(rotation);
    return r ? `rotate(${r}deg)` : 'none';
  }

  isSideways(rotation?: number): boolean {
    const r = this.normalizeRotation(rotation);
    return r === 90 || r === 270;
  }

  /** Gallery slot narrower than 80px — hide comments control. */
  isExhibitionCardNarrow(width: number | null | undefined): boolean {
    const w = Number(width);
    return Number.isFinite(w) && w > 0 && w < 80;
  }

  /** Like count only when slot is wider than 55px. */
  showExhibitionLikeCount(width: number | null | undefined): boolean {
    const w = Number(width);
    return Number.isFinite(w) && w > 55;
  }

  /** Displayed width/height for focus media sizing (post-rotation). */
  mediaAspectValue(card: ExhibitionCard): number {
    if (card?.natW && card?.natH) {
      return this.isSideways(card.rotation)
        ? card.natH / card.natW
        : card.natW / card.natH;
    }
    if (this.commentsTargetId === card._id && this.focusFlipOrigin) {
      const { width, height } = this.focusFlipOrigin;
      if (width > 1 && height > 1) {
        return width / height;
      }
    }
    return 0.75;
  }

  /** Post-rotate visual aspect ratio (width / height). */
  mediaAspectRatio(card: ExhibitionCard): string {
    if (!card.natW || !card.natH) {
      return '3 / 4';
    }
    return this.isSideways(card.rotation)
      ? `${card.natH} / ${card.natW}`
      : `${card.natW} / ${card.natH}`;
  }

  isFocusPanoramic(card: ExhibitionCard): boolean {
    return isWallDesktopViewport() && isWallImagePanoramic(card.natW, card.natH, card.rotation);
  }

  isFocusHeightCapped(card: ExhibitionCard): boolean {
    return this.focusCappedIds.has(card._id);
  }

  onCardImageLoad(event: Event, card: ExhibitionCard): void {
    const img = event.target as HTMLImageElement;
    if (isWallPlaceholderThumb(img?.currentSrc || img?.src)) {
      this.hideCardWithoutImage(card);
      return;
    }
    if (img?.naturalWidth && img.naturalHeight) {
      const prevW = card.natW;
      const prevH = card.natH;
      card.natW = img.naturalWidth;
      card.natH = img.naturalHeight;
      card.imgLoaded = true;
      if (
        wallDisplayedAspectChanged(
          prevW,
          prevH,
          card.natW,
          card.natH,
          card.rotation
        )
      ) {
        this.applyRowsLayout(true);
      }
      if (this.commentsTargetId === card._id && card.commentsOpen) {
        this.scheduleFocusAdapt();
      }
      return;
    }
    card.imgLoaded = true;
    if (this.commentsTargetId === card._id && card.commentsOpen) {
      this.scheduleFocusAdapt();
    }
  }

  onCardImageError(_event: Event, card: ExhibitionCard): void {
    this.hideCardWithoutImage(card);
  }

  private hideCardWithoutImage(card: ExhibitionCard): void {
    if (!card?._id || !this.cards.some((c) => c._id === card._id)) {
      return;
    }
    this.cards = this.cards.filter((c) => c._id !== card._id);
    this.applyRowsLayout(true);
  }

  private normalizeRotation(value?: number): 0 | 90 | 180 | 270 {
    const n = Number(value);
    if (n === 90 || n === 180 || n === 270) {
      return n;
    }
    return 0;
  }

  goToMyWall(): void {
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    this.router.navigate(['/wall']);
  }

  goToNewOrder(): void {
    if (!this.isAuth) {
      this.dialogService.onOpenLoginDialog('', '');
      return;
    }
    const printingService = localStorage.getItem('printingService');
    const branch = localStorage.getItem('branch');
    if (
      printingService &&
      printingService !== 'null' &&
      printingService !== '' &&
      branch &&
      branch !== 'null' &&
      branch !== ''
    ) {
      this.dialogService.onOpenRightPlaceDialog();
    } else if (branch && branch !== 'null' && branch !== '') {
      this.router.navigate(['/branch']);
    } else {
      const current = this.router.url.split('?')[0];
      if (current !== '/' && current !== '') {
        this.router.navigate(['/']);
      }
    }
  }

  openContactAuthor(
    userID?: string,
    authorName?: string,
    event?: Event
  ): void {
    event?.stopPropagation();
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    const id = String(userID || '').trim();
    if (!id || this.isOwnUser(id)) {
      return;
    }
    this.dialogService.onOpenWallContactDialog({
      userID: id,
      authorName: String(authorName || '').trim(),
    });
  }

  openAuthorWall(card: ExhibitionCard, event?: Event): void {
    event?.stopPropagation();
    const userId = String(card?.userID || '').trim();
    if (!userId || userId === this.filterUserId) {
      return;
    }
    const preview = this.ownerProfileFromCard(card);
    pendingOwnerPreview = preview;
    this.router.navigate(['/exhibition', userId], {
      state: { exhibitionOwnerPreview: preview },
    });
  }

  /** Open "הקיר של" from a comment — carries the avatar URL comments already have. */
  openCommentAuthorWall(comment: ExhibitionComment, event?: Event): void {
    event?.stopPropagation();
    const userId = String(comment?.userID || '').trim();
    if (!userId || userId === this.filterUserId || !this.isValidUserId(userId)) {
      return;
    }
    const imageCandidates = Array.isArray(comment?.authorImageCandidates)
      ? comment.authorImageCandidates
          .map((u) => String(u || '').trim())
          .filter(Boolean)
      : [];
    const imageUrl =
      String(comment?.authorImageURL || '').trim() || imageCandidates[0] || '';
    const preview: ExhibitionOwnerProfile = {
      userID: userId,
      authorName: String(comment?.authorName || '').trim(),
      authorImageURL: imageUrl,
      authorImageCandidates: this.mergeImageCandidates(imageCandidates, imageUrl),
      about: '',
      lookingForWork: false,
      offeringHelp: false,
      allowContact: true,
      academySerialName: '',
    };
    pendingOwnerPreview = preview;
    this.router.navigate(['/exhibition', userId], {
      state: { exhibitionOwnerPreview: preview },
    });
  }

  backToExhibition(): void {
    this.router.navigate(['/exhibition']);
  }

  private onRouteUserId(userId: string): void {
    if (userId && !this.isValidUserId(userId)) {
      this.router.navigate(['/exhibition'], { replaceUrl: true });
      return;
    }
    const nextFilter = userId || null;
    if (nextFilter === this.filterUserId) {
      const inflight = !!this.feedSub && !this.feedSub.closed;
      if (!inflight && !this.cards.length && !this.loadError) {
        this.loadInitial();
      }
      return;
    }
    this.filterUserId = nextFilter;
    this.filterProfile = nextFilter
      ? this.resolveOwnerPreview(nextFilter) || {
          userID: nextFilter,
          authorName: '',
          authorImageURL: '',
          authorImageCandidates: [],
          about: '',
          lookingForWork: false,
          offeringHelp: false,
          allowContact: true,
          academySerialName: '',
        }
      : null;
    if (nextFilter) {
      this.filterPanelOpen = false;
    } else {
      this.loadAcademyOptions();
    }
    this.scrollExhibitionTop();
    if (nextFilter) {
      this.hydrateFilterOwnerAvatar(nextFilter);
    } else {
      this.ownerImageSub?.unsubscribe();
    }
    this.loadInitial();
  }

  /**
   * Load owner avatar without waiting for a backend deploy that adds image
   * fields on exhibition/profile: auth memory (own wall) + /user/:id + comments.
   */
  private hydrateFilterOwnerAvatar(userId: string): void {
    const id = String(userId || '').trim();
    if (!id || !this.isValidUserId(id) || !this.filterProfile) {
      return;
    }
    const me = String(this.authService.getUserId() || '').trim();
    if (me && me === id) {
      const fromAuth = wallProfileImageCandidates(this.authService.getUser());
      if (fromAuth.length) {
        this.applyFilterOwnerImage(fromAuth[0], fromAuth);
      }
    }
    this.ownerImageSub?.unsubscribe();
    this.ownerImageSub = this.usersService.getUser(id).subscribe({
      next: (user: any) => {
        if (
          String(this.filterUserId || '').trim() !== id ||
          !this.filterProfile
        ) {
          return;
        }
        const candidates = wallProfileImageCandidates(user);
        if (!candidates.length) {
          return;
        }
        this.applyFilterOwnerImage(candidates[0], candidates);
      },
      error: () => {},
    });
  }

  private isValidUserId(userId: string): boolean {
    return /^[a-f\d]{24}$/i.test(String(userId || '').trim());
  }

  private ownerProfileFromCard(card: ExhibitionCard): ExhibitionOwnerProfile {
    const imageCandidates = Array.isArray(card?.authorImageCandidates)
      ? card.authorImageCandidates.map((u) => String(u || '').trim()).filter(Boolean)
      : [];
    let imageUrl =
      String(card?.authorImageURL || '').trim() || imageCandidates[0] || '';
    // Comments API already returns authorImageURL on api-dev; reuse if card lacks it.
    if (!imageUrl && card?.comments?.length) {
      const ownerId = String(card?.userID || '').trim();
      const flat: ExhibitionComment[] = [];
      const walk = (list: ExhibitionComment[]) => {
        for (const c of list || []) {
          flat.push(c);
          if (c.replies?.length) {
            walk(c.replies);
          }
        }
      };
      walk(card.comments);
      const hit = flat.find(
        (c) =>
          String(c.userID || '').trim() === ownerId &&
          String(c.authorImageURL || '').trim()
      );
      if (hit) {
        imageUrl = String(hit.authorImageURL || '').trim();
        const fromComment = Array.isArray(hit.authorImageCandidates)
          ? hit.authorImageCandidates
              .map((u) => String(u || '').trim())
              .filter(Boolean)
          : [];
        if (fromComment.length) {
          imageCandidates.push(...fromComment);
        } else if (imageUrl) {
          imageCandidates.push(imageUrl);
        }
      }
    }
    const uniqueCandidates = this.mergeImageCandidates(imageCandidates, imageUrl);
    return {
      userID: String(card?.userID || '').trim(),
      authorName: String(card?.authorName || '').trim(),
      authorImageURL: imageUrl || uniqueCandidates[0] || '',
      authorImageCandidates: uniqueCandidates,
      about: '',
      lookingForWork: card?.lookingForWork === true,
      offeringHelp: card?.offeringHelp === true,
      allowContact: card?.allowContact !== false,
      academySerialName: String(card?.academySerialName || '').trim(),
    };
  }

  private resolveOwnerPreview(userId: string): ExhibitionOwnerProfile | null {
    const id = String(userId || '').trim();
    if (!id) {
      return null;
    }
    const fromPending = pendingOwnerPreview;
    pendingOwnerPreview = null;
    if (this.isOwnerPreviewForUser(fromPending, id)) {
      return fromPending;
    }
    const fromState = this.readOwnerPreviewState();
    if (this.isOwnerPreviewForUser(fromState, id)) {
      return fromState;
    }
    const card = this.cards.find((c) => String(c.userID || '').trim() === id);
    return card ? this.ownerProfileFromCard(card) : null;
  }

  private readOwnerPreviewState(): ExhibitionOwnerProfile | null {
    const nav = this.router.getCurrentNavigation();
    const raw =
      nav?.extras?.state?.['exhibitionOwnerPreview'] ||
      (typeof history !== 'undefined'
        ? history.state?.exhibitionOwnerPreview
        : null);
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    return {
      userID: String(raw.userID || '').trim(),
      authorName: String(raw.authorName || '').trim(),
      authorImageURL: String(raw.authorImageURL || '').trim(),
      authorImageCandidates: Array.isArray(raw.authorImageCandidates)
        ? raw.authorImageCandidates.map((u: unknown) => String(u || '').trim()).filter(Boolean)
        : [],
      about: String(raw.about || '').trim(),
      lookingForWork: raw.lookingForWork === true,
      offeringHelp: raw.offeringHelp === true,
      allowContact: raw.allowContact !== false,
      academySerialName: String(raw.academySerialName || '').trim(),
    };
  }

  private isOwnerPreviewForUser(
    preview: ExhibitionOwnerProfile | null,
    userId: string
  ): preview is ExhibitionOwnerProfile {
    return !!preview && String(preview.userID || '').trim() === userId;
  }

  private scrollExhibitionTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }

  private redirectToExhibition(): void {
    this.router.navigate(['/exhibition'], { replaceUrl: true });
  }

  onLike(card: ExhibitionCard, event?: Event): void {
    event?.stopPropagation();
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    if (card.liking) {
      return;
    }
    card.liking = true;
    const prevLiked = card.likedByMe;
    const prevCount = card.likeCount;
    const prevLikers = [...(card.recentLikers || [])];
    const prevAllLikers = [...(card.likers || [])];
    this.applyLocalLike(card, !prevLiked);
    this.syncCardLikers(card, !prevLiked);

    this.likeSubs[card._id]?.unsubscribe();
    this.likeSubs[card._id] = this.wallApi.toggleItemLike(card._id).subscribe({
      next: (res) => {
        card.likedByMe = res.liked === true;
        card.likeCount = Number(res.likeCount) || 0;
        if (Array.isArray(res.recentLikers)) {
          card.recentLikers = res.recentLikers;
        }
        if (card.commentsMounted) {
          this.loadItemLikers(card);
        }
        if (card.likeCount <= 3) {
          card.likersExpanded = false;
        }
        card.liking = false;
      },
      error: () => {
        card.likedByMe = prevLiked;
        card.likeCount = prevCount;
        card.recentLikers = prevLikers;
        card.likers = prevAllLikers;
        card.liking = false;
        this.showSnack('wall.exhibition.like-error');
      },
    });
  }

  onCommentLike(
    card: ExhibitionCard,
    comment: ExhibitionComment,
    event?: Event
  ): void {
    event?.stopPropagation();
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    if (comment.liking) {
      return;
    }
    comment.liking = true;
    const prevLiked = comment.likedByMe === true;
    const prevCount = Number(comment.likeCount) || 0;
    const prevLikers = [...(comment.recentLikers || [])];
    const prevAllLikers = [...(comment.likers || [])];
    this.applyLocalLike(comment, !prevLiked);
    this.syncCommentLikers(comment, !prevLiked);

    this.commentLikeSubs[comment._id]?.unsubscribe();
    this.commentLikeSubs[comment._id] = this.wallApi
      .toggleCommentLike(card._id, comment._id)
      .subscribe({
        next: (res) => {
          comment.likedByMe = res.liked === true;
          comment.likeCount = Number(res.likeCount) || 0;
          if (Array.isArray(res.recentLikers)) {
            comment.recentLikers = res.recentLikers;
          }
          if (comment.likeCount <= 0) {
            comment.likersExpanded = false;
          } else if (comment.likersExpanded) {
            this.loadCommentLikers(card, comment);
          }
          comment.liking = false;
        },
        error: () => {
          comment.likedByMe = prevLiked;
          comment.likeCount = prevCount;
          comment.recentLikers = prevLikers;
          comment.likers = prevAllLikers;
          comment.liking = false;
          this.showSnack('wall.exhibition.like-error');
        },
      });
  }

  topLikers(names?: string[]): string[] {
    return (names || []).filter((n) => !!String(n || '').trim()).slice(0, 3);
  }

  hasMoreCommenters(card: ExhibitionCard): boolean {
    return (Number(card.commenterCount) || 0) > 3;
  }

  visibleLikers(card: ExhibitionCard): string[] {
    const names = (card.likers?.length ? card.likers : card.recentLikers || [])
      .map((n) => String(n || '').trim())
      .filter(Boolean);
    return card.likersExpanded ? names : names.slice(0, 3);
  }

  canExpandLikers(card: ExhibitionCard): boolean {
    return (Number(card.likeCount) || 0) > 3;
  }

  toggleLikersExpand(card: ExhibitionCard, event?: Event): void {
    event?.stopPropagation();
    card.likersExpanded = !card.likersExpanded;
    if (card.likersExpanded) {
      this.loadItemLikers(card);
    }
  }

  private loadItemLikers(card: ExhibitionCard): void {
    this.loadLikersSubs[card._id]?.unsubscribe();
    this.loadLikersSubs[card._id] = this.wallApi
      .getItemLikes(card._id)
      .subscribe({
        next: (res) => {
          card.likers = Array.isArray(res?.names) ? res.names : [];
          if (res?.likeCount != null) {
            card.likeCount = Number(res.likeCount) || 0;
          }
          this.cdr.detectChanges();
          if (this.commentsTargetId === card._id && card.commentsOpen) {
            this.scheduleFocusAdapt();
          }
        },
        error: () => {},
      });
  }

  commentLikers(comment: ExhibitionComment): string[] {
    return (comment.likers?.length ? comment.likers : comment.recentLikers || [])
      .map((n) => String(n || '').trim())
      .filter(Boolean);
  }

  toggleCommentLikers(
    card: ExhibitionCard,
    comment: ExhibitionComment,
    event?: Event
  ): void {
    event?.stopPropagation();
    comment.likersExpanded = !comment.likersExpanded;
    if (comment.likersExpanded) {
      this.loadCommentLikers(card, comment);
    }
  }

  private loadCommentLikers(
    card: ExhibitionCard,
    comment: ExhibitionComment
  ): void {
    this.loadCommentLikersSubs[comment._id]?.unsubscribe();
    this.loadCommentLikersSubs[comment._id] = this.wallApi
      .getCommentLikes(card._id, comment._id)
      .subscribe({
        next: (res) => {
          comment.likers = Array.isArray(res?.names) ? res.names : [];
          if (res?.likeCount != null) {
            comment.likeCount = Number(res.likeCount) || 0;
          }
          if (comment.likeCount <= 0) {
            comment.likersExpanded = false;
          }
        },
        error: () => {},
      });
  }

  private syncCommentLikers(comment: ExhibitionComment, liked: boolean): void {
    const mine = String(localStorage.getItem('userName') || '').trim();
    if (!mine) {
      return;
    }
    const names = [
      ...(comment.likers?.length ? comment.likers : comment.recentLikers || []),
    ];
    comment.likers = liked
      ? [mine, ...names.filter((n) => n !== mine)]
      : names.filter((n) => n !== mine);
  }

  ensureCommentersTip(card: ExhibitionCard): void {
    if ((Number(card.commentCount) || 0) <= 0) {
      return;
    }
    if (this.topLikers(card.recentCommenters).length) {
      return;
    }
    if (card.comments?.length) {
      this.refreshRecentCommenters(card);
      return;
    }
    if (card.commentsLoading) {
      return;
    }
    this.prefetchCommenterSubs[card._id]?.unsubscribe();
    this.prefetchCommenterSubs[card._id] = this.wallApi
      .getItemComments(card._id)
      .subscribe({
        next: (res) => {
          const incoming = Array.isArray(res?.comments) ? res.comments : [];
          if (!card.comments.length) {
            card.comments = this.nestComments(incoming);
            card.commentCount = this.countComments(card);
          }
          this.refreshRecentCommenters(card);
          this.syncOwnerAvatarFromComments(card.comments);
        },
        error: () => {},
      });
  }

  private applyLocalLike(
    target: {
      likedByMe?: boolean;
      likeCount?: number;
      recentLikers?: string[];
    },
    liked: boolean
  ): void {
    const prevCount = Number(target.likeCount) || 0;
    target.likedByMe = liked;
    target.likeCount = Math.max(0, prevCount + (liked ? 1 : -1));
    const mine = String(localStorage.getItem('userName') || '').trim();
    const names = [...(target.recentLikers || [])];
    if (!mine) {
      return;
    }
    if (liked) {
      target.recentLikers = [mine, ...names.filter((n) => n !== mine)].slice(
        0,
        3
      );
    } else {
      target.recentLikers = names.filter((n) => n !== mine);
    }
  }

  private syncCardLikers(card: ExhibitionCard, liked: boolean): void {
    const mine = String(localStorage.getItem('userName') || '').trim();
    if (!mine) {
      return;
    }
    const names = [
      ...(card.likers?.length ? card.likers : card.recentLikers || []),
    ];
    card.likers = liked
      ? [mine, ...names.filter((n) => n !== mine)]
      : names.filter((n) => n !== mine);
  }

  toggleComments(card: ExhibitionCard, event?: Event): void {
    event?.stopPropagation();
    if (card.commentsOpen || card.commentsMounted) {
      this.closeCommentsPanel(card);
      return;
    }
    this.cards.forEach((c) => {
      if (c._id !== card._id && (c.commentsOpen || c.commentsMounted)) {
        this.closeCommentsPanel(c);
      }
    });
    this.openCommentsPanel(card);
  }

  openCommentsFromImage(card: ExhibitionCard, event?: Event): void {
    event?.stopPropagation();
    this.toggleComments(card, event);
  }

  /** Gradient / overlay chrome opens focus; like button is excluded. */
  openCommentsFromOverlay(card: ExhibitionCard, event?: Event): void {
    const el =
      event?.target instanceof Element
        ? event.target
        : (event?.target as Node | null)?.parentElement;
    if (el?.closest('.eazix-exhibition-card-like-btn')) {
      return;
    }
    this.openCommentsFromImage(card, event);
  }

  private openCommentsPanel(card: ExhibitionCard): void {
    clearTimeout(this.commentsCloseTimers[card._id]);
    delete this.commentsCloseTimers[card._id];

    const originEl =
      (this.host.nativeElement.querySelector(
        `[data-wall-item-id="${card._id}"]`
      ) as HTMLElement | null) ||
      (this.host.nativeElement.querySelector(
        `[data-wall-card-id="${card._id}"]`
      ) as HTMLElement | null);
    const originRect = originEl?.getBoundingClientRect();
    this.focusFlipOrigin = originRect
      ? {
          left: originRect.left,
          top: originRect.top,
          width: Math.max(1, originRect.width),
          height: Math.max(1, originRect.height),
        }
      : null;

    if (!card.comments.length) {
      this.loadComments(card);
    }

    if (this.focusContentNeedsMaxHeight(card)) {
      this.focusCappedIds.add(card._id);
    } else {
      this.focusCappedIds.delete(card._id);
    }

    card.commentsMounted = true;
    card.commentsOpen = true;
    this.commentsTargetId = card._id;
    this.lockBodyScroll();
    this.loadItemLikers(card);
    this.cdr.detectChanges();

    // Wait for compose/mat-field + comments chrome, then pin once and FLIP.
    this.openFocusAfterChromeReady(card._id, !!card.comments.length);
  }

  /** Drop in-flight open FLIP settle so a mid-open close cannot snap to CSS top:0. */
  private cancelPendingFocusOpen(): void {
    if (this.focusOpenRaf != null) {
      cancelAnimationFrame(this.focusOpenRaf);
      this.focusOpenRaf = null;
    }
    if (this.focusSettleTimer != null) {
      clearTimeout(this.focusSettleTimer);
      this.focusSettleTimer = null;
    }
  }

  private scheduleFocusOpenSettle(cardId: string): void {
    if (this.focusSettleTimer != null) {
      clearTimeout(this.focusSettleTimer);
    }
    this.focusSettleTimer = setTimeout(() => {
      this.focusSettleTimer = null;
      this.settleFocusPanelToCss(cardId);
    }, this.commentsAnimMs);
  }

  private openFocusAfterChromeReady(cardId: string, scrollCommentsTop: boolean): void {
    this.cancelPendingFocusOpen();
    this.ngZone.runOutsideAngular(() => {
      this.focusOpenRaf = requestAnimationFrame(() => {
        this.focusOpenRaf = null;
        this.ngZone.run(() => {
          if (this.commentsTargetId !== cardId) {
            return;
          }
          const card = this.cards.find((c) => c._id === cardId);
          if (!card?.commentsOpen) {
            return;
          }
          if (this.focusContentNeedsMaxHeight(card)) {
            this.focusCappedIds.add(cardId);
          } else {
            this.focusCappedIds.delete(cardId);
          }
          const focusPanel = this.getFocusPanelEl(cardId);
          if (focusPanel) {
            void focusPanel.offsetHeight;
            this.syncFocusPanelPinnedHeight(cardId, true);
          }
          this.focusFlipUntil = Date.now() + this.commentsAnimMs + 48;
          this.playFocusFlipOpen(cardId);
          this.scheduleFocusOpenSettle(cardId);
          if (scrollCommentsTop) {
            setTimeout(() => this.scrollCommentsToTop(cardId), 0);
          }
        });
      });
    });
  }

  private closeCommentsPanel(card: ExhibitionCard): void {
    if (!card.commentsMounted || !card.commentsOpen) {
      return;
    }
    this.discardCardEdits(card);
    card.workTitleExpanded = false;
    card.workDescExpanded = false;
    this.clearStatusTip(card);
    clearTimeout(this.commentsCloseTimers[card._id]);
    this.cancelPendingFocusOpen();
    this.focusFlipUntil = 0;

    // Pin CSS-centered layout to pixels so removing .card-open does not jump.
    this.pinFocusPanelFromRect(card._id);
    const focusPanel = this.getFocusPanelEl(card._id);
    const panelStart = focusPanel?.getBoundingClientRect() ?? null;
    const mediaStart = focusPanel
      ? wallFocusMediaEl(focusPanel)?.getBoundingClientRect() ?? null
      : null;

    // Collapse comments first, then animate the image panel back to the slot.
    card.commentsOpen = false;
    this.cdr.detectChanges();

    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        const played = this.playFocusFlipClose(card._id, mediaStart, panelStart);
        this.ngZone.run(() => {
          this.commentsCloseTimers[card._id] = setTimeout(() => {
            card.commentsMounted = false;
            this.clearFocusFlipStyles(card._id);
            if (this.commentsTargetId === card._id) {
              this.commentsTargetId = null;
              this.focusFlipOrigin = null;
              this.focusCappedIds.delete(card._id);
              this.unlockBodyScroll();
            }
            delete this.commentsCloseTimers[card._id];
          }, played ? this.commentsAnimMs : 0);
        });
      });
    });
  }

  private focusDialogWidth(cardId?: string): number {
    if (typeof window === 'undefined') {
      return 560;
    }
    if (!isWallDesktopViewport()) {
      return window.innerWidth;
    }
    const wide = window.innerWidth >= 900;
    const standard = Math.min(window.innerWidth * (wide ? 0.9 : 0.94), wide ? 640 : 560);
    const id = cardId || this.commentsTargetId;
    const card = id ? this.cards.find((c) => c._id === id) : undefined;
    if (card && isWallImagePanoramic(card.natW, card.natH, card.rotation)) {
      return Math.max(standard, window.innerWidth - 100);
    }
    return standard;
  }

  private focusDialogMaxHeight(): number {
    if (typeof window === 'undefined') {
      return 900;
    }
    if (!isWallDesktopViewport()) {
      const visible = window.visualViewport?.height || window.innerHeight;
      return Math.max(1, Math.round(visible * 0.9));
    }
    return Math.min(window.innerHeight - 24, 900);
  }

  commentsLoadReservePx(card: { commentCount?: number; comments?: unknown[] }): number {
    return wallCommentsLoadReservePx(
      card.commentCount,
      Array.isArray(card.comments) ? card.comments.length : 0
    );
  }

  private focusContentNeedsMaxHeight(card: ExhibitionCard): boolean {
    const n = Math.max(
      Number(card.commentCount) || 0,
      Array.isArray(card.comments) ? card.comments.length : 0
    );
    if (n <= 0 || typeof window === 'undefined') {
      return false;
    }
    const maxH = this.focusDialogMaxHeight();
    const imgMax = Math.min(window.innerHeight * 0.58, 520);
    const { w, h } = wallDisplayedSize(card.natW, card.natH, card.rotation);
    const panelW = this.focusDialogWidth(card._id);
    let imgH = imgMax;
    if (w > 0 && h > 0) {
      const ar = w / h;
      const imgW = Math.min(panelW, Math.max(1, imgMax * ar));
      imgH = Math.min(imgMax, imgW / Math.max(0.01, ar));
    }
    return imgH + 230 + n * 72 > maxH - 4;
  }

  private focusDialogLeft(finalW: number): number {
    if (typeof window === 'undefined' || !isWallDesktopViewport()) {
      return 0;
    }
    return Math.max(0, (window.innerWidth - finalW) / 2);
  }

  private focusDialogTop(finalH: number): number {
    if (typeof window === 'undefined') {
      return 12;
    }
    if (!isWallDesktopViewport()) {
      return Math.max(0, window.innerHeight - finalH);
    }
    const margin = 12;
    const maxTop = Math.max(margin, window.innerHeight - finalH - margin);
    const centered = (window.innerHeight - finalH) / 2;
    return Math.max(margin, Math.min(centered, maxTop));
  }

  private getFocusPanelEl(cardId: string): HTMLElement | null {
    return this.host.nativeElement.querySelector(
      `[data-focus-panel="${cardId}"]`
    ) as HTMLElement | null;
  }

  private getFocusSlotEl(cardId: string): HTMLElement | null {
    return this.host.nativeElement.querySelector(
      `[data-wall-card-id="${cardId}"]`
    ) as HTMLElement | null;
  }

  private clearFocusFlipStyles(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel) {
      return;
    }
    panel.style.left = '';
    panel.style.top = '';
    panel.style.width = '';
    panel.style.maxWidth = '';
    panel.style.right = '';
    panel.style.bottom = '';
    panel.style.maxHeight = '';
    panel.style.removeProperty('height');
    panel.style.transform = '';
    panel.style.transformOrigin = '';
    panel.style.transition = '';
    panel.style.borderRadius = '';
    wallFocusClearMediaFlipStyles(panel);
    panel.classList.remove('eazix-exhibition-card-focus-returning');
    panel.classList.remove('eazix-exhibition-card-focus-opening');
    panel.classList.remove('eazix-exhibition-card-focus-body-scroll');
  }

  private hydrateFocusNatFromPanel(cardId: string): ExhibitionCard | undefined {
    const card = this.cards.find((c) => c._id === cardId);
    const panel = this.getFocusPanelEl(cardId);
    if (!card || !panel || (card.natW && card.natH)) {
      return card;
    }
    const dims = wallFocusNatFromPanelImg(panel);
    if (dims) {
      card.natW = dims.natW;
      card.natH = dims.natH;
      card.imgLoaded = true;
    }
    return card;
  }

  /** Snapshot visual box to inline pixels (close FLIP, when CSS centering would drop). */
  private pinFocusPanelFromRect(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel || typeof window === 'undefined') {
      return;
    }
    const r = panel.getBoundingClientRect();
    panel.style.transition = 'none';
    panel.style.transform = 'none';
    panel.classList.remove('eazix-exhibition-card-focus-opening');
    wallFocusPinPanelToBox(panel, r);
    void panel.offsetWidth;
  }

  private focusCommentsReservePx(card: ExhibitionCard): number {
    return wallCommentsLoadReservePx(
      card.commentCount,
      Array.isArray(card.comments) ? card.comments.length : 0
    );
  }

  private measureFocusNaturalHeight(cardId: string): number {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel) {
      return 1;
    }
    this.hydrateFocusNatFromPanel(cardId);
    const card = this.cards.find((c) => c._id === cardId);
    const reserve = card ? this.focusCommentsReservePx(card) : 0;
    const contentH = this.intrinsicFocusContentHeight(panel);
    return wallFocusNaturalPanelHeight(panel, contentH, reserve);
  }

  private isFocusPanelPinned(cardId: string): boolean {
    if (Date.now() < this.focusFlipUntil) {
      return true;
    }
    const panel = this.getFocusPanelEl(cardId);
    if (!panel) {
      return false;
    }
    return (
      !!panel.style.left ||
      !!panel.style.bottom ||
      panel.style.getPropertyPriority('height') === 'important'
    );
  }

  private syncFocusPanelPinnedHeight(cardId: string, force = false): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel || typeof window === 'undefined') {
      return;
    }
    if (!force && Date.now() < this.focusFlipUntil) {
      this.focusAdaptQueued = true;
      return;
    }
    this.hydrateFocusNatFromPanel(cardId);
    this.syncFocusFillFromOverflow(cardId, force);
    const finalW = this.focusDialogWidth(cardId);
    const maxH = this.focusDialogMaxHeight();
    const card = this.cards.find((c) => c._id === cardId);
    const reserve = card ? this.focusCommentsReservePx(card) : 0;
    const contentH = this.intrinsicFocusContentHeight(panel);
    const naturalH = wallFocusNaturalPanelHeight(panel, contentH, reserve);
    const finalH = this.focusCappedIds.has(cardId)
      ? maxH
      : Math.min(naturalH, maxH);
    panel.style.transition = 'none';
    // Pixel left/top already center the dialog — neutralize CSS translate(-50%, -50%).
    panel.style.transform = 'none';
    panel.style.width = `${finalW}px`;
    panel.style.maxWidth = `${finalW}px`;
    panel.style.maxHeight = `${maxH}px`;
    panel.style.setProperty('height', `${finalH}px`, 'important');
    if (!isWallDesktopViewport()) {
      panel.style.left = '0px';
      panel.style.right = '0px';
      panel.style.top = 'auto';
      panel.style.bottom = '0px';
    } else {
      panel.style.left = `${this.focusDialogLeft(finalW)}px`;
      panel.style.top = `${this.focusDialogTop(finalH)}px`;
      panel.style.right = '';
      panel.style.bottom = '';
    }
    void panel.offsetWidth;
  }

  private beginFocusOpenFlip(cardId: string): void {
    this.playFocusFlipOpen(cardId);
    this.scheduleFocusOpenSettle(cardId);
  }

  private afterFocusContentLayout(cardId: string): void {
    if (Date.now() < this.focusFlipUntil) {
      this.focusAdaptQueued = true;
      return;
    }
    requestAnimationFrame(() => {
      this.cdr.detectChanges();
      requestAnimationFrame(() => {
        if (this.isFocusPanelPinned(cardId)) {
          this.syncFocusPanelPinnedHeight(cardId, true);
        }
        this.quietAdaptFocusHeight(cardId);
      });
    });
  }

  /** Let CSS size/place the open card so it tracks viewport changes. */
  private settleFocusPanelToCss(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    const card = this.cards.find((c) => c._id === cardId);
    if (!panel || this.commentsTargetId !== cardId || !card?.commentsOpen) {
      return;
    }
    const pinnedH =
      parseFloat(panel.style.height) ||
      panel.getBoundingClientRect().height ||
      0;
    this.focusFlipUntil = 0;
    this.focusAdaptQueued = false;
    panel.style.transition = 'none';
    panel.style.left = '';
    panel.style.top = '';
    panel.style.right = '';
    panel.style.bottom = '';
    panel.style.width = '';
    panel.style.maxWidth = '';
    panel.style.maxHeight = '';
    panel.style.transform = '';
    panel.style.borderRadius = '';
    wallFocusClearMediaFlipStyles(panel);
    panel.classList.remove('eazix-exhibition-card-focus-opening');
    // Keep pinned height with !important so CSS `height: auto !important` cannot snap.
    if (pinnedH > 1) {
      panel.style.setProperty('height', `${Math.round(pinnedH)}px`, 'important');
    }
    void panel.offsetWidth;
    panel.style.transition = '';
    if (card?.commentsLoading) {
      this.syncFocusPanelPinnedHeight(cardId, true);
      return;
    }
    this.syncFocusBodyScroll(cardId);
  }

  /**
   * Fit the open panel to content up to the viewport max; scroll only when capped.
   * Avoid locking a short height with an inner scrollport (phantom mobile scroll).
   */
  private syncFocusBodyScroll(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel) {
      return;
    }
    const card = this.cards.find((c) => c._id === cardId);
    const reserve = card ? this.focusCommentsReservePx(card) : 0;
    const contentH = this.intrinsicFocusContentHeight(panel);
    const naturalH = wallFocusNaturalPanelHeight(panel, contentH, reserve);
    const maxH = this.focusDialogMaxHeight();
    const needsCap = naturalH > maxH + 1;
    const finalH = needsCap ? maxH : Math.max(1, Math.min(naturalH, maxH));

    if (needsCap) {
      this.focusCappedIds.add(cardId);
      panel.classList.add('eazix-exhibition-card-focus-fill');
      panel.classList.remove('eazix-exhibition-card-focus-body-scroll');
      panel.style.removeProperty('height');
    } else {
      this.focusCappedIds.delete(cardId);
      panel.classList.remove('eazix-exhibition-card-focus-fill');
      panel.classList.remove('eazix-exhibition-card-focus-body-scroll');
      panel.style.setProperty('height', `${Math.round(finalH)}px`, 'important');
    }
  }

  private quietAdaptFocusHeight(cardId: string): void {
    if (this.commentsTargetId !== cardId || Date.now() < this.focusFlipUntil) {
      return;
    }
    const panel = this.getFocusPanelEl(cardId);
    const card = this.cards.find((c) => c._id === cardId);
    if (!panel || !card?.commentsOpen) {
      return;
    }
    this.syncFocusBodyScroll(cardId);
  }

  private intrinsicFocusContentHeight(panel: HTMLElement): number {
    const media = panel.querySelector(
      '.eazix-exhibition-card-media'
    ) as HTMLElement | null;
    const comments = panel.querySelector(
      '.eazix-exhibition-comments-clip'
    ) as HTMLElement | null;
    const fold = comments?.querySelector(
      '.eazix-exhibition-comments-fold'
    ) as HTMLElement | null;
    const compose = panel.querySelector(
      ':scope > .eazix-exhibition-compose-dock'
    ) as HTMLElement | null;
    let h = 0;
    if (media) {
      h += media.offsetHeight;
    }
    if (fold) {
      h += fold.scrollHeight;
    } else if (comments) {
      h += comments.scrollHeight;
    }
    if (compose) {
      h += compose.offsetHeight;
    }
    return h > 0 ? h : panel.scrollHeight;
  }

  private syncFocusFillFromOverflow(cardId: string, force = false): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel || typeof window === 'undefined') {
      return;
    }
    if (!force && Date.now() < this.focusFlipUntil) {
      return;
    }
    const maxH = this.focusDialogMaxHeight();
    const card = this.cards.find((c) => c._id === cardId);
    const contentH = this.intrinsicFocusContentHeight(panel);
    if (contentH > maxH + 1) {
      this.focusCappedIds.add(cardId);
      panel.classList.add('eazix-exhibition-card-focus-fill');
    } else if (!card?.commentsLoading) {
      this.focusCappedIds.delete(cardId);
      panel.classList.remove('eazix-exhibition-card-focus-fill');
    }
  }

  private scheduleFocusAdapt(): void {
    const id = this.commentsTargetId;
    if (!id) {
      return;
    }
    if (Date.now() < this.focusFlipUntil) {
      this.focusAdaptQueued = true;
      return;
    }
    if (this.focusAdaptRaf != null) {
      cancelAnimationFrame(this.focusAdaptRaf);
    }
    this.focusAdaptRaf = requestAnimationFrame(() => {
      this.focusAdaptRaf = null;
      this.ngZone.run(() => {
        const cardId = this.commentsTargetId;
        if (!cardId) {
          return;
        }
        const card = this.cards.find((c) => c._id === cardId);
        if (!card?.commentsOpen) {
          return;
        }
        this.cdr.detectChanges();
        if (this.isFocusPanelPinned(cardId)) {
          this.syncFocusPanelPinnedHeight(cardId);
          return;
        }
        this.quietAdaptFocusHeight(cardId);
      });
    });
  }

  /** Keep the fixed panel within the viewport; extra comments scroll inside. */
  private relayoutFocusPanel(cardId: string, force = false): void {
    const panel = this.getFocusPanelEl(cardId);
    if (!panel || typeof window === 'undefined') {
      return;
    }
    if (!force && Date.now() < this.focusFlipUntil) {
      return;
    }
    const finalW = this.focusDialogWidth(cardId);
    const maxH = this.focusDialogMaxHeight();
    panel.style.transition = 'none';
    panel.style.transform = 'none';
    panel.style.width = `${finalW}px`;
    panel.style.maxWidth = `${finalW}px`;
    panel.style.maxHeight = `${maxH}px`;
    const measured = panel.getBoundingClientRect();
    const finalH = Math.min(Math.max(1, measured.height), maxH);
    panel.style.left = `${this.focusDialogLeft(finalW)}px`;
    panel.style.top = `${this.focusDialogTop(finalH)}px`;
  }

  private playFocusFlipOpen(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    const origin = this.focusFlipOrigin;
    if (!panel || !origin || typeof window === 'undefined') {
      return;
    }

    if (this.focusCappedIds.has(cardId)) {
      panel.classList.add('eazix-exhibition-card-focus-fill');
    } else {
      panel.classList.remove('eazix-exhibition-card-focus-fill');
    }

    panel.style.transition = 'none';
    // Neutralize CSS centering transform so the measured final box matches pin geometry.
    panel.style.transform = 'none';
    void panel.offsetWidth;
    const panelLast = panel.getBoundingClientRect();
    const media = wallFocusMediaEl(panel);
    const mediaLast = media?.getBoundingClientRect() ?? null;
    this.focusFlipUntil = Date.now() + this.commentsAnimMs;
    const played = wallFocusPlayOpenFromSlot(
      panel,
      origin,
      this.commentsAnimMs,
      'eazix-exhibition-card-focus-opening',
      panelLast,
      mediaLast,
      { slotRadius: '0', openRadius: '0' }
    );
    if (!played) {
      panel.classList.remove('eazix-exhibition-card-focus-opening');
    }
  }

  /** @returns true when a FLIP close animation was started */
  private playFocusFlipClose(
    cardId: string,
    mediaStart?: DOMRect | null,
    panelStart?: DOMRect | null
  ): boolean {
    const panel = this.getFocusPanelEl(cardId);
    const origin = this.focusFlipOrigin;
    if (!panel || !origin) {
      return false;
    }
    return wallFocusPlayCloseToSlot(
      panel,
      origin,
      this.commentsAnimMs,
      'eazix-exhibition-card-focus-returning',
      mediaStart,
      panelStart,
      { slotRadius: '0', openRadius: '0' }
    );
  }

  closeActiveCommentsFocus(): void {
    if (!this.commentsTargetId) {
      return;
    }
    const card = this.cards.find((c) => c._id === this.commentsTargetId);
    if (card) {
      this.closeCommentsPanel(card);
    }
  }

  onFocusCloseClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.closeActiveCommentsFocus();
  }

  private handleFocusOutsidePointerDown(event: PointerEvent): void {
    if (this.tryCloseOpenReply(event)) {
      return;
    }
    if (!this.commentsTargetId || !this.isCommentsFocusVisible()) {
      return;
    }
    const node = event.target instanceof Node ? event.target : null;
    if (!node) {
      return;
    }
    const el = node instanceof Element ? node : node.parentElement;
    // Close / backdrop live outside the panel; let their own handlers run.
    // Closing here on pointerdown removes the X before click and the event
    // falls through to the navbar (user menu).
    if (
      el?.closest(
        '.eazix-exhibition-focus-close, .eazix-exhibition-focus-backdrop, .cdk-overlay-container, .cdk-overlay-pane'
      )
    ) {
      return;
    }
    const panel = this.getFocusPanelEl(this.commentsTargetId);
    if (panel && panel.contains(node)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.closeActiveCommentsFocus();
  }

  closeCommentsPanelViaUi(card: ExhibitionCard, event?: Event): void {
    event?.stopPropagation();
    this.closeCommentsPanel(card);
  }

  isCommentsFocusVisible(): boolean {
    if (!this.commentsTargetId) {
      return false;
    }
    const card = this.cards.find((c) => c._id === this.commentsTargetId);
    return !!card?.commentsMounted;
  }

  isFocusCloseVisible(): boolean {
    if (!this.commentsTargetId) {
      return false;
    }
    const card = this.cards.find((c) => c._id === this.commentsTargetId);
    return !!card?.commentsOpen;
  }

  private lockBodyScroll(): void {
    if (typeof document === 'undefined' || this.bodyOverflowPrev != null) {
      return;
    }
    this.bodyOverflowPrev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    if (typeof document === 'undefined' || this.bodyOverflowPrev == null) {
      return;
    }
    document.body.style.overflow = this.bodyOverflowPrev;
    this.bodyOverflowPrev = null;
  }

  loadComments(card: ExhibitionCard): void {
    if (card.comments.length) {
      return;
    }
    const inflight = this.loadCommentsSubs[card._id];
    if (card.commentsLoading && inflight && !inflight.closed) {
      return;
    }
    card.commentsLoading = true;
    this.prefetchCommenterSubs[card._id]?.unsubscribe();
    this.loadCommentsSubs[card._id]?.unsubscribe();
    this.loadCommentsSubs[card._id] = this.wallApi
      .getItemComments(card._id)
      .subscribe({
        next: (res) => {
          const incoming = Array.isArray(res?.comments) ? res.comments : [];
          card.comments = this.nestComments(incoming);
          card.commentCount = this.countComments(card);
          this.refreshRecentCommenters(card);
          this.syncOwnerAvatarFromComments(card.comments);
          card.commentsLoading = false;
          this.cdr.detectChanges();
          if (this.commentsTargetId === card._id && card.commentsOpen) {
            this.afterFocusContentLayout(card._id);
          }
          setTimeout(() => this.scrollCommentsToTop(card._id), 0);
        },
        error: () => {
          card.commentsLoading = false;
          this.showSnack('wall.exhibition.comments-error');
          this.cdr.detectChanges();
          if (this.commentsTargetId === card._id && card.commentsOpen) {
            this.afterFocusContentLayout(card._id);
          }
        },
      });
  }

  submitComment(card: ExhibitionCard): void {
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    const text = String(card.draftComment || '').trim();
    if (!text || card.commenting) {
      return;
    }
    card.commenting = true;
    this.commentSubs[card._id]?.unsubscribe();
    this.commentSubs[card._id] = this.wallApi
      .addItemComment(card._id, text)
      .subscribe({
        next: (res) => {
          if (res?.comment) {
            card.comments = [
              this.hydrateComment(res.comment),
              ...card.comments,
            ];
          }
          card.commentCount =
            res?.commentCount != null
              ? res.commentCount
              : this.countComments(card);
          this.refreshRecentCommenters(card);
          card.draftComment = '';
          card.commenting = false;
          this.composeHadTextIds.delete(card._id);
          setTimeout(() => {
            this.resetComposeField(card._id);
            this.scrollCommentsToTop(card._id);
          }, 0);
        },
        error: () => {
          card.commenting = false;
          this.showSnack('wall.exhibition.comment-error');
        },
      });
  }

  deleteComment(card: ExhibitionCard, comment: ExhibitionComment): void {
    if (!this.isMyComment(comment) || comment.saving) {
      return;
    }
    this.deleteCommentSubs[comment._id]?.unsubscribe();
    this.deleteCommentSubs[comment._id] = this.wallApi
      .deleteItemComment(card._id, comment._id)
      .subscribe({
        next: (res) => {
          this.removeCommentFromCard(card, comment._id);
          this.forgetCommentTextLayout(comment._id);
          card.commentCount =
            res?.commentCount != null
              ? res.commentCount
              : this.countComments(card);
          this.refreshRecentCommenters(card);
          this.afterCommentsLayoutChange();
        },
        error: () => {
          this.showSnack('wall.exhibition.comment-delete-error');
        },
      });
  }

  startEditComment(
    card: ExhibitionCard,
    comment: ExhibitionComment,
    event?: Event
  ): void {
    event?.stopPropagation();
    if (!this.isMyComment(comment) || comment.saving) {
      return;
    }
    this.flattenComments(card).forEach((c) => {
      if (c._id !== comment._id) {
        this.cancelEditComment(c);
      }
      c.confirmDelete = false;
    });
    comment.editing = true;
    comment.draftText = comment.text;
    setTimeout(() => {
      this.resizeCommentFieldById(comment._id);
      this.afterCommentsLayoutChange();
    }, 0);
  }

  cancelEditComment(comment: ExhibitionComment): void {
    if (comment.saving) {
      return;
    }
    comment.editing = false;
    comment.draftText = comment.text;
    comment.confirmDelete = false;
    this.afterCommentsLayoutChange();
  }

  isCommentDirty(comment: ExhibitionComment): boolean {
    return (
      String(comment.draftText || '').trim() !== String(comment.text || '').trim()
    );
  }

  resizeCommentField(event: Event): void {
    const el = event.target as HTMLTextAreaElement | null;
    if (el) {
      this.fitCommentTextarea(el);
    }
  }

  onComposeInput(event: Event, card: ExhibitionCard): void {
    this.resizeCommentField(event);
    const hasText = !!(card?.draftComment || '').trim();
    const hadText = this.composeHadTextIds.has(card._id);
    if (hasText) {
      this.composeHadTextIds.add(card._id);
    } else {
      this.composeHadTextIds.delete(card._id);
    }
    if (
      hasText !== hadText &&
      this.commentsTargetId === card._id &&
      card.commentsOpen
    ) {
      this.afterCommentsLayoutChange();
    }
  }

  onReplyInput(
    event: Event,
    card: ExhibitionCard,
    comment: ExhibitionComment,
  ): void {
    this.resizeCommentField(event);
    if (!comment?.replying || !comment?._id) {
      return;
    }
    this.cdr.detectChanges();
    this.queueScrollReplySendIntoView(card, comment._id);
  }

  private queueScrollReplySendIntoView(
    card: ExhibitionCard,
    commentId: string,
  ): void {
    if (typeof window === 'undefined') {
      return;
    }
    const root = this.getFocusPanelEl(card._id) || this.host.nativeElement;
    const reveal = () => {
      const btn = root.querySelector(
        `[data-reply-send="${commentId}"]`,
      ) as HTMLElement | null;
      const compose = root.querySelector(
        `[data-reply-compose="${commentId}"]`,
      ) as HTMLElement | null;
      const target = btn || compose;
      if (target) {
        scrollWallFocusElIntoView(target, { pinEnd: true });
      }
    };
    this.cdr.detectChanges();
    window.requestAnimationFrame(() => {
      reveal();
      window.setTimeout(reveal, 50);
      window.setTimeout(reveal, 150);
    });
  }

  private scrollAnyOpenReplySendIntoView(): void {
    for (const card of this.cards) {
      const open = (card.comments || []).find((c) => c.replying);
      if (open?._id) {
        this.queueScrollReplySendIntoView(card, open._id);
      }
    }
  }

  private resizeCommentFieldById(commentId: string): void {
    const el = document.querySelector(
      `[data-comment-edit="${commentId}"]`
    ) as HTMLTextAreaElement | null;
    if (el) {
      this.fitCommentTextarea(el);
      el.focus();
    }
  }

  private fitCommentTextarea(el: HTMLTextAreaElement): void {
    const maxPx = 100;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxPx)}px`;
  }

  private resetComposeField(cardId: string): void {
    const el = document.querySelector(
      `[data-compose-edit="${cardId}"]`
    ) as HTMLTextAreaElement | null;
    if (el) {
      el.style.height = 'auto';
    }
  }

  private scrollCommentsToTop(cardId: string): void {
    const panel = this.getFocusPanelEl(cardId);
    const scroller = panel?.querySelector(
      '.eazix-exhibition-focus-scroll'
    ) as HTMLElement | null;
    if (scroller) {
      scroller.scrollTop = 0;
    }
    const el = document.querySelector(
      `[data-comments-list="${cardId}"]`
    ) as HTMLElement | null;
    if (el) {
      el.scrollTop = 0;
    }
    if (this.commentsTargetId === cardId) {
      this.scheduleFocusAdapt();
    }
  }

  private scrollOpenedCardIntoView(cardId: string): void {
    const media = this.host.nativeElement.querySelector(
      `[data-wall-item-id="${cardId}"]`
    ) as HTMLElement | null;
    scrollWallMediaToVisibleTop(media, () => this.queueFeedCatchUp());
    // Comments fold continues expanding after scroll starts.
    setTimeout(() => this.queueFeedCatchUp(), this.commentsAnimMs + 40);
  }

  saveEditComment(card: ExhibitionCard, comment: ExhibitionComment): void {
    if (!this.isMyComment(comment) || comment.saving) {
      return;
    }
    const text = String(comment.draftText || '').trim();
    if (!text || text === comment.text) {
      this.cancelEditComment(comment);
      return;
    }
    comment.saving = true;
    this.editCommentSubs[comment._id]?.unsubscribe();
    this.editCommentSubs[comment._id] = this.wallApi
      .updateItemComment(card._id, comment._id, text)
      .subscribe({
        next: (res) => {
          comment.text = res?.comment?.text != null ? res.comment.text : text;
          comment.editing = false;
          comment.confirmDelete = false;
          comment.saving = false;
          this.afterCommentsLayoutChange();
        },
        error: () => {
          comment.saving = false;
          this.showSnack('wall.exhibition.comment-edit-error');
        },
      });
  }

  isMyComment(comment: ExhibitionComment): boolean {
    return (
      this.isAuth &&
      !!comment?.userID &&
      comment.userID === this.authService.getUserId()
    );
  }

  hasOpenReply(card: ExhibitionCard): boolean {
    return (card?.comments || []).some((c) => c.replying);
  }

  private findOpenReplyComment(card: ExhibitionCard): ExhibitionComment | null {
    return (card?.comments || []).find((c) => c.replying) || null;
  }

  closeOpenReply(card: ExhibitionCard): void {
    const open = this.findOpenReplyComment(card);
    if (!open) {
      return;
    }
    open.replying = false;
    this.scheduleFocusAdapt();
    this.cdr.markForCheck();
  }

  private tryCloseOpenReply(event: PointerEvent): boolean {
    const node = event.target instanceof Node ? event.target : null;
    if (!node) {
      return false;
    }
    const el = node instanceof Element ? node : node.parentElement;
    if (el?.closest('.cdk-overlay-container, .cdk-overlay-pane')) {
      return false;
    }
    if (
      el?.closest(
        '.eazix-exhibition-reply-compose, [data-reply-send], .eazix-exhibition-reply-btn'
      )
    ) {
      return false;
    }
    for (const card of this.cards) {
      if (this.hasOpenReply(card)) {
        this.closeOpenReply(card);
        return true;
      }
    }
    return false;
  }

  toggleReply(
    card: ExhibitionCard,
    parent: ExhibitionComment,
    event?: Event
  ): void {
    event?.stopPropagation();
    if (parent.parentId) {
      return;
    }
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    const next = !parent.replying;
    card.comments.forEach((c) => {
      if (c._id !== parent._id) {
        c.replying = false;
      }
    });
    parent.replying = next;
    if (next) {
      setTimeout(() => {
        this.focusReplyField(parent._id);
        this.queueScrollReplySendIntoView(card, parent._id);
      }, 0);
    } else {
      (event?.currentTarget as HTMLElement)?.blur?.();
    }
    this.scheduleFocusAdapt();
  }

  submitReply(card: ExhibitionCard, parent: ExhibitionComment): void {
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    const text = String(parent.draftReply || '').trim();
    if (!text || parent.commentingReply || parent.parentId) {
      return;
    }
    parent.commentingReply = true;
    this.replySubs[parent._id]?.unsubscribe();
    this.replySubs[parent._id] = this.wallApi
      .addItemComment(card._id, text, parent._id)
      .subscribe({
        next: (res) => {
          const reply = res?.comment
            ? this.hydrateComment(res.comment)
            : null;
          if (reply) {
            parent.replies = [reply, ...(parent.replies || [])];
          }
          card.commentCount =
            res?.commentCount != null
              ? res.commentCount
              : this.countComments(card);
          this.refreshRecentCommenters(card);
          parent.draftReply = '';
          parent.commentingReply = false;
          parent.replying = false;
        },
        error: () => {
          parent.commentingReply = false;
          this.showSnack('wall.exhibition.comment-error');
        },
      });
  }

  private hydrateComment(raw: ExhibitionComment): ExhibitionComment {
    return {
      ...raw,
      parentId: raw.parentId || null,
      replies: Array.isArray(raw.replies)
        ? raw.replies.map((r) => this.hydrateComment(r))
        : [],
      editing: false,
      draftText: raw.text,
      saving: false,
      confirmDelete: false,
      replying: false,
      draftReply: '',
      commentingReply: false,
      likeCount: Number(raw.likeCount) || 0,
      likedByMe: raw.likedByMe === true,
      recentLikers: Array.isArray(raw.recentLikers) ? raw.recentLikers : [],
      likers: Array.isArray(raw.recentLikers) ? [...raw.recentLikers] : [],
      likersExpanded: false,
      liking: false,
    };
  }

  private nestComments(incoming: ExhibitionComment[]): ExhibitionComment[] {
    const byId = new Map<string, ExhibitionComment>();
    incoming.forEach((raw) => {
      const c = this.hydrateComment(raw);
      c.replies = [];
      byId.set(c._id, c);
    });
    const roots: ExhibitionComment[] = [];
    incoming.forEach((raw) => {
      const c = byId.get(raw._id);
      if (!c) {
        return;
      }
      const parentId = c.parentId ? String(c.parentId) : '';
      const parent = parentId ? byId.get(parentId) : null;
      if (parent && !parent.parentId) {
        parent.replies = parent.replies || [];
        parent.replies.push(c);
      } else {
        roots.push(c);
      }
    });
    return roots;
  }

  private flattenComments(card: ExhibitionCard): ExhibitionComment[] {
    const out: ExhibitionComment[] = [];
    (card.comments || []).forEach((c) => {
      out.push(c);
      if (c.replies?.length) {
        out.push(...c.replies);
      }
    });
    return out;
  }

  private discardCardEdits(card: ExhibitionCard): void {
    card.draftComment = '';
    this.composeHadTextIds.delete(card._id);
    this.flattenComments(card).forEach((c) => {
      if (!c.saving) {
        c.editing = false;
        c.draftText = c.text;
      }
      if (!c.commentingReply) {
        c.replying = false;
        c.draftReply = '';
      }
      c.confirmDelete = false;
    });
    this.resetComposeField(card._id);
  }

  private countComments(card: ExhibitionCard): number {
    return this.flattenComments(card).length;
  }

  private refreshRecentCommenters(card: ExhibitionCard): void {
    const all = this.flattenComments(card)
      .slice()
      .sort((a, b) => {
        const ta = new Date(a.created as string).getTime();
        const tb = new Date(b.created as string).getTime();
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      });
    const names: string[] = [];
    const seen = new Set<string>();
    all.forEach((c) => {
      const id = String(c.userID || '');
      const name = String(c.authorName || '').trim();
      const key = id || name;
      if (!key || seen.has(key)) {
        return;
      }
      seen.add(key);
      if (name && names.length < 3) {
        names.push(name);
      }
    });
    card.recentCommenters = names;
    card.commenterCount = seen.size;
  }

  private removeCommentFromCard(card: ExhibitionCard, commentId: string): void {
    if ((card.comments || []).some((c) => c._id === commentId)) {
      card.comments = card.comments.filter((c) => c._id !== commentId);
      return;
    }
    card.comments.forEach((c) => {
      if (c.replies?.some((r) => r._id === commentId)) {
        c.replies = c.replies.filter((r) => r._id !== commentId);
      }
    });
  }

  private focusReplyField(parentId: string): void {
    const el = document.querySelector(
      `[data-reply-edit="${parentId}"]`
    ) as HTMLTextAreaElement | null;
    if (el) {
      this.fitCommentTextarea(el);
      el.focus();
    }
  }

  retryLoad(): void {
    this.loadInitial();
  }

  private loadInitial(): void {
    this.isLoading = true;
    this.loadError = false;
    this.cards = [];
    this.layoutRows = [];
    this.layoutTotalHeight = 0;
    this.lastLayoutWidth = 0;
    this.seenIds.clear();
    this.hasMore = true;
    this.commentsTargetId = null;
    this.unlockBodyScroll();
    this.setFeedSkeletons(true);
    this.fetchPage(true);
  }

  private loadMore(): void {
    if (this.isLoading || this.isLoadingMore || !this.hasMore) {
      return;
    }
    this.isLoadingMore = true;
    this.setFeedSkeletons(false);
    this.fetchPage(false);
  }

  private fetchPage(isInitial: boolean): void {
    this.feedSub?.unsubscribe();
    this.feedSub = this.wallApi
      .getExhibitionFeed(
        Array.from(this.seenIds),
        this.pageSize,
        this.filterUserId,
        this.currentFeedFilters()
      )
      .subscribe({
        next: (res) => {
          if (this.filterUserId) {
            if (!res?.profile) {
              this.clearFeedSkeletons();
              this.isLoading = false;
              this.isLoadingMore = false;
              this.redirectToExhibition();
              return;
            }
            const prevImageUrl = String(
              this.filterProfile?.authorImageURL || ''
            ).trim();
            const prevImageCandidates = Array.isArray(
              this.filterProfile?.authorImageCandidates
            )
              ? [...this.filterProfile.authorImageCandidates]
              : [];
            this.filterProfile = {
              userID: String(res.profile.userID || this.filterUserId),
              authorName:
                String(res.profile.authorName || '').trim() ||
                String(this.filterProfile?.authorName || ''),
              authorImageURL: '',
              authorImageCandidates: [],
              about:
                res.profile.about != null
                  ? String(res.profile.about).trim()
                  : '',
              lookingForWork: res.profile.lookingForWork === true,
              offeringHelp: res.profile.offeringHelp === true,
              allowContact: res.profile.allowContact !== false,
              academySerialName:
                String(res.profile.academySerialName || '').trim() ||
                String(this.filterProfile?.academySerialName || ''),
            };
            const feedImages = Array.isArray(res.profile.authorImageCandidates)
              ? res.profile.authorImageCandidates
                  .map((u) => String(u || '').trim())
                  .filter(Boolean)
              : [];
            const fromItem = Array.isArray(res?.items)
              ? res.items.find(
                  (item) =>
                    String(item?.userID || '').trim() ===
                    String(this.filterUserId || '').trim()
                )
              : null;
            const itemImages = Array.isArray(fromItem?.authorImageCandidates)
              ? fromItem.authorImageCandidates
                  .map((u) => String(u || '').trim())
                  .filter(Boolean)
              : [];
            this.applyFilterOwnerImage(
              String(res.profile.authorImageURL || '').trim() ||
                String(fromItem?.authorImageURL || '').trim() ||
                prevImageUrl,
              this.mergeImageCandidates(
                feedImages,
                itemImages,
                prevImageCandidates,
                prevImageUrl
              )
            );
            if (this.filterUserId) {
              this.hydrateFilterOwnerAvatar(this.filterUserId);
            }
          }
          const incoming = Array.isArray(res?.items) ? res.items : [];
          incoming.forEach((item) => {
            if (item?._id) {
              this.seenIds.add(item._id);
            }
          });
          const mapped = incoming
            .filter((item) => item?._id && !isWallPlaceholderThumb(resolveWallItemThumb(item)))
            .filter((item) => this.itemMatchesFeedFilters(item))
            .map((item, index) => this.toCard(item, this.cards.length + index));
          this.hasMore = res?.hasMore === true;
          this.loadError = false;
          if (!mapped.length && this.hasMore) {
            this.clearFeedSkeletons();
            this.isLoading = false;
            this.isLoadingMore = false;
            this.loadMore();
            return;
          }
          const hydrateGen = ++this.feedHydrateGen;
          // Probe natural sizes before appending so collage never paints with defaultAspect.
          hydrateWallItemImageSizes(mapped).then(() => {
            this.ngZone.run(() => {
              if (hydrateGen !== this.feedHydrateGen) {
                return;
              }
              this.cards = [...this.cards, ...mapped];
              this.clearFeedSkeletons();
              this.isLoading = false;
              this.isLoadingMore = false;
              // Flush view so collage exists; measure stable inner width (not host without padding).
              this.cdr.detectChanges();
              this.applyRowsLayout(true);
              this.prefetchOwnerAvatarFromComments();
              setTimeout(() => {
                if (hydrateGen !== this.feedHydrateGen) {
                  return;
                }
                this.bindLayoutObserver();
                this.setupObserver();
                this.queueFeedCatchUp();
              }, 0);
              setTimeout(() => this.queueFeedCatchUp(), 100);
              setTimeout(() => this.queueFeedCatchUp(), 400);
            });
          });
        },
        error: () => {
          this.clearFeedSkeletons();
          this.isLoading = false;
          this.isLoadingMore = false;
          this.loadError = isInitial;
          if (!isInitial) {
            this.showSnack('wall.exhibition.load-error');
          }
        },
      });
  }

  private applyRowsLayout(force = false): void {
    const el = this.collageRef?.nativeElement;
    // Always-present inner column — same width as collage; avoids first layout on host
    // (before side padding) which causes a visible width jump when cards appear.
    const innerEl = this.host.nativeElement.querySelector(
      '.eazix-exhibition-inner'
    ) as HTMLElement | null;
    const width =
      el?.clientWidth ||
      innerEl?.clientWidth ||
      this.host.nativeElement.clientWidth ||
      (typeof window !== 'undefined' ? Math.min(window.innerWidth, 1200) : 960);

    if (!force && this.lastLayoutWidth > 0) {
      const delta = Math.abs(width - this.lastLayoutWidth);
      if (delta < GALLERY_ROWS_TUNABLES.resizeThresholdPx && this.layoutRows.length) {
        return;
      }
    }

    const works: Array<ExhibitionCard | WallSkeletonWork> = [
      ...this.cards,
      ...this.skeletonSlots,
    ];
    const result = this.rowsLayout.layout(works, width);
    // Re-bind work refs to live card objects (layout clones input refs by identity).
    const byId = new Map(this.cards.map((c) => [String(c._id), c]));
    this.layoutRows = result.rows.map((row) => ({
      ...row,
      items: row.items.map((item) => ({
        ...item,
        work: byId.get(String(item.work._id)) || item.work,
      })),
    }));
    this.layoutTotalHeight = result.totalHeight;
    this.layoutGap = result.gap;
    this.lastLayoutWidth = width;
  }

  /** Keep page scrollbar gutter reserved so content width doesn't shrink when feed appears. */
  private lockStableScrollbarGutter(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document
      .querySelector('mat-sidenav-content')
      ?.classList.add('wall-stable-scrollbar');
  }

  private unlockStableScrollbarGutter(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document
      .querySelector('mat-sidenav-content')
      ?.classList.remove('wall-stable-scrollbar');
  }

  private scheduleRowsRelayoutFromResize(): void {
    if (!this.cards.length && !this.skeletonSlots.length) {
      return;
    }
    if (this.resizeLayoutTimer) {
      clearTimeout(this.resizeLayoutTimer);
    }
    this.resizeLayoutTimer = setTimeout(() => {
      this.resizeLayoutTimer = null;
      this.ngZone.run(() => this.applyRowsLayout(false));
    }, GALLERY_ROWS_TUNABLES.resizeDebounceMs);
  }

  private measureFeedLayoutWidth(): number {
    const el = this.collageRef?.nativeElement;
    const innerEl = this.host.nativeElement.querySelector(
      '.eazix-exhibition-inner'
    ) as HTMLElement | null;
    return (
      el?.clientWidth ||
      innerEl?.clientWidth ||
      this.host.nativeElement.clientWidth ||
      (typeof window !== 'undefined' ? Math.min(window.innerWidth, 1200) : 960)
    );
  }

  private setFeedSkeletons(initial: boolean): void {
    const width = this.measureFeedLayoutWidth();
    const rows = initial ? 2 : 1;
    const count = wallSkeletonCount(width, rows);
    this.skeletonSlots = makeWallSkeletonWorks(
      count,
      initial ? 'exh-skel' : 'exh-skel-more',
      initial ? undefined : this.cards
    );
    this.cdr.detectChanges();
    this.applyRowsLayout(true);
  }

  private clearFeedSkeletons(): void {
    if (!this.skeletonSlots.length) {
      return;
    }
    this.skeletonSlots = [];
  }

  /** After images reveal real sizes, refresh row weights if comments aren't open. */
  private scheduleBalancedRelayout(): void {
    if (this.balanceRelayoutTimer) {
      clearTimeout(this.balanceRelayoutTimer);
    }
    this.balanceRelayoutTimer = setTimeout(() => {
      this.balanceRelayoutTimer = null;
      if (!this.cards.length) {
        return;
      }
      if (this.cards.some((c) => c.commentsOpen || c.commentsMounted)) {
        return;
      }
      this.applyRowsLayout(true);
      setTimeout(() => this.queueFeedCatchUp(), 0);
    }, 280);
  }

  private toCard(item: ExhibitionFeedItem, index: number): ExhibitionCard {
    return {
      ...item,
      thumbUrl: resolveWallItemThumb(item),
      userID: String(item.userID || ''),
      rotation: this.normalizeRotation(item.rotation),
      academySerialName: item.academySerialName || '',
      lookingForWork: item.lookingForWork === true,
      offeringHelp: item.offeringHelp === true,
      allowContact: item.allowContact !== false,
      likeCount: Number(item.likeCount) || 0,
      commentCount: Number(item.commentCount) || 0,
      commenterCount: Number(item.commenterCount) || 0,
      likedByMe: item.likedByMe === true,
      recentLikers: Array.isArray(item.recentLikers) ? item.recentLikers : [],
      likers: Array.isArray(item.recentLikers) ? [...item.recentLikers] : [],
      likersExpanded: false,
      workTitleExpanded: false,
      workDescExpanded: false,
      statusTip: null,
      recentCommenters: Array.isArray(item.recentCommenters)
        ? item.recentCommenters
        : [],
      commentsOpen: false,
      commentsMounted: false,
      commentsLoading: false,
      comments: [],
      draftComment: '',
      liking: false,
      commenting: false,
      imgLoaded: false,
    };
  }

  private readonly feedPrefetchMarginPx = (): number =>
    typeof window !== 'undefined' ? Math.round(window.innerHeight * 1.4) : 900;

  private setupObserver(): void {
    this.observer?.disconnect();
    const nodes = this.sentinels?.toArray() || [];
    if (!nodes.length) {
      this.queueFeedCatchUp();
      return;
    }
    // Prefetch ~1.4 viewports before the end so new works land before the user arrives.
    const margin = this.feedPrefetchMarginPx();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.ngZone.run(() => this.loadMore());
        }
      },
      { root: null, rootMargin: `${margin}px 0px`, threshold: 0 }
    );
    nodes.forEach((ref) => {
      if (ref?.nativeElement) {
        this.observer?.observe(ref.nativeElement);
      }
    });
    this.queueFeedCatchUp();
  }

  /**
   * Layout/scroll can leave the end sentinel near view without a fresh IO event —
   * prefetch explicitly when the sentinel is within the early-load margin.
   */
  private queueFeedCatchUp(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.feedCatchUpRaf != null) {
      cancelAnimationFrame(this.feedCatchUpRaf);
    }
    this.feedCatchUpRaf = window.requestAnimationFrame(() => {
      this.feedCatchUpRaf = window.requestAnimationFrame(() => {
        this.feedCatchUpRaf = null;
        this.ensureFeedCatchUp();
      });
    });
  }

  private ensureFeedCatchUp(): void {
    kickWallLazyImagesNearView(this.host.nativeElement);
    if (this.isLoading || this.isLoadingMore || !this.hasMore) {
      return;
    }
    if (!this.isFeedPrefetchNearView()) {
      return;
    }
    this.loadMore();
  }

  private isFeedPrefetchNearView(): boolean {
    const sentinels = this.sentinels?.toArray() || [];
    if (sentinels.length) {
      return sentinels.some((ref) =>
        ref?.nativeElement ? this.isElementNearView(ref.nativeElement) : false
      );
    }
    // Fallback: last real card in the collage.
    const cards = this.host.nativeElement.querySelectorAll(
      '.eazix-exhibition-card'
    );
    if (!cards.length) {
      return false;
    }
    return this.isElementNearView(cards[cards.length - 1] as HTMLElement);
  }

  private isElementNearView(el: HTMLElement): boolean {
    const margin = this.feedPrefetchMarginPx();
    const r = el.getBoundingClientRect();
    return (
      r.top < window.innerHeight + margin &&
      r.bottom > -margin &&
      r.left < window.innerWidth + margin &&
      r.right > -margin
    );
  }

  private showSnack(key: string): void {
    this.translate.get(key).subscribe((msg) => {
      this.snackBar.open(msg, '', { duration: 3500 });
    });
  }
}
